import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { shouldRenderDesktop } from '../utils/deviceDetection';
import { useWebNotification } from '../hooks/useWebNotification';
import formatChatTime from '../utils/formatChatTime';
import axios from 'axios';
import HandlerChat from './HandlerChat';
import { toast } from 'sonner';
import FlagIcons from '../components/FlagIcons';
import InstantAccessG from '../components/InstantAccessG';
import InstantAccessPT from '../components/InstantAccessPT';
import InstantAccessN from '../components/InstantAccessN';
import PendingManager from '../components/PendingManager';
import InquiryPanel from '../components/InquiryPanel';
import InstantAccessPW from '../components/InstantAccessPW';
import { avatarPalFor } from '../utils/avatarPalette';
import StatusManagement from '../components/StatusManagement';
import CustomerDetailsPanel from '../components/CustomerDetailsPanel';
import { 
  MagnifyingGlass, 
  DotsThreeVertical,
  UserCircle,
  Chats,
  TrendUp,
  SignOut,
  Gear,
  Broom,
  Users,
  Megaphone,
  UsersThree,
  ArrowLeft,
  Star,
  ArrowClockwise,
  Copy,
  WhatsappLogo,
  Image as ImageIcon,
  PushPin
} from '@phosphor-icons/react';
import BlockedClientsSection from '../components/BlockedClientsSection';
import MBSetupBadge from '../components/MBSetupBadge';
import { useMBList } from '../hooks/useMBList';
import BulkAssignMBModal from '../components/BulkAssignMBModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

import { startPolling, onVisibilityFetch } from '../utils/pollingKeepAlive';
import { List as VirtualList } from 'react-window';
import { AutoSizer } from 'react-virtualized-auto-sizer';
import socket, { connectSocket, onNewMessage, offNewMessage, onMessagesRead, offMessagesRead } from '../utils/socket';
import { truncatePreview, formatLastMessagePreview } from '../utils/previewTruncate';
import { prefetchRoom } from '../utils/messagePrefetch';
import MobileWhatsAppHandlerView from '../components/MobileWhatsAppHandlerView';
import useSectionCycleKeys from '../hooks/useSectionCycleKeys';

// Batch H, point 3 — module-level so the array reference stays stable
// across renders (avoids re-attaching the cycle-key listeners every render).
const MAIN_SECTIONS = ['deposit', 'withdrawal', 'support', 'inquiry'];

const HandlerDashboard = () => {
  const { user, logout, updateUser } = useAuth();
  // Point 3 — silent auto-reload replaced by a controllable UpdateBanner popup
  // (mounted globally in App.js). No forced reload here anymore.

  // Session-36: Refetch fresh permissions from server on mount so admin's
  // toggle (Handler Management → 🔑 Settings Powers) reflects in the
  // dashboard without requiring the handler to log out first. localStorage
  // user often has stale `permissions` from previous session — this replaces
  // it with the current server truth. Also listens for socket-broadcasted
  // permission changes so an admin toggle instantly hides/shows options.
  useEffect(() => {
    if (!user?.id || user.role === 'super_admin') return;
    let cancelled = false;
    const fetchPerms = async () => {
      try {
        const r = await axios.get(`${API}/users/handler/${user.id}/permissions`);
        if (cancelled) return;
        const fresh = r.data?.permissions || {};
        // Only update if something actually changed to avoid render loops
        const current = user.permissions || {};
        const changed = Object.keys({ ...fresh, ...current }).some(k => !!current[k] !== !!fresh[k]);
        if (changed && typeof updateUser === 'function') {
          updateUser({ ...user, permissions: fresh });
        }
      } catch { /* silent */ }
    };
    fetchPerms();
    const onPermsUpdated = (payload) => {
      if (payload?.handler_id === user.id && payload.permissions && typeof updateUser === 'function') {
        updateUser({ ...user, permissions: { ...(user.permissions || {}), ...payload.permissions } });
      }
    };
    socket.on('handler_permissions_updated', onPermsUpdated);
    return () => { cancelled = true; socket.off('handler_permissions_updated', onPermsUpdated); };
  }, [user?.id]);
  const navigate = useNavigate();
  const location = useLocation();
  const { showNotification, playNotificationSound, updateAppBadge } = useWebNotification();
  
  // Check if inquiry handler - simplified interface
  const isInquiryHandler = user?.role === 'inquiry_handler';
  // Deposit handler gets admin-like powers
  const isDepositHandler = user?.role === 'deposit_handler';
  // Third-party withdraw handler — group-ONLY interface, no chats/directory/analytics.
  const isThirdPartyHandler = user?.role === 'third_party_withdraw_handler';
  
  const [chats, setChats] = useState(() => {
    try { const c = localStorage.getItem('hdl_chats'); return c ? JSON.parse(c) : []; } catch { return []; }
  });
  // Cache chats per chat_type so switching Deposit ⇄ Withdrawal ⇄ Support is
  // instant on subsequent visits (background refresh continues via polling).
  const chatsByTypeRef = useRef(() => {
    try {
      const raw = localStorage.getItem('hdl_chats_by_type');
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  });
  // useRef with a function initializer runs the function once; call it to unbox
  if (typeof chatsByTypeRef.current === 'function') chatsByTypeRef.current = chatsByTypeRef.current();
  const [groups, setGroups] = useState(() => {
    try { const g = localStorage.getItem('hdl_groups'); return g ? JSON.parse(g) : []; } catch { return []; }
  });
  const [filteredChats, setFilteredChats] = useState([]);
  // Progressive rendering — only paint first N rows initially, IntersectionObserver loads more.
  const [visibleCount, setVisibleCount] = useState(30);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState(() => {
    try {
      // Third-party handler is FORCED into the groups tab and cannot leave it.
      if (user?.role === 'third_party_withdraw_handler') return 'groups';
      const saved = sessionStorage.getItem('hdl_active_tab');
      if (saved && ['all', 'unread', 'favourites', 'groups', 'archive'].includes(saved)) return saved;
    } catch (_) {}
    return 'all';
  });
  const [loading, setLoading] = useState(() => {
    try { return !localStorage.getItem('hdl_chats'); } catch { return true; }
  });
  const [bottomTab, setBottomTab] = useState('chats');

  // Third-party withdraw handler is FORCED to stay on the Groups tab and the
  // bottom "chats" nav slot — no chats, no directory, no analytics for them.
  useEffect(() => {
    if (user?.role === 'third_party_withdraw_handler') {
      if (activeTab !== 'groups') setActiveTab('groups');
      if (bottomTab !== 'chats') setBottomTab('chats');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, bottomTab, user?.role]);

  const [showMenu, setShowMenu] = useState(false);
  const [showPendingManager, setShowPendingManager] = useState(false);
  // Persist MB filter across chat-open → back navigation and page refreshes.
  // Mobile handlers regularly work inside a single MB — losing the filter on
  // every trip into a chat was the #1 UX complaint.
  const MB_FILTER_KEY = 'handler-mb-filter';
  const liveMBs = useMBList();
  const [mbFilter, setMbFilterState] = useState(() => {
    try { return sessionStorage.getItem(MB_FILTER_KEY) || 'all'; }
    catch { return 'all'; }
  }); // 'all' | 'MB7'..'MB11'
  const setMbFilter = useCallback((v) => {
    setMbFilterState(v);
    try {
      if (v && v !== 'all') sessionStorage.setItem(MB_FILTER_KEY, v);
      else sessionStorage.removeItem(MB_FILTER_KEY);
    } catch {}
  }, []);
  const [withdrawGroups, setWithdrawGroups] = useState([]);
  const [showWithdrawGroupsMenu, setShowWithdrawGroupsMenu] = useState(false);
  const [gMenuPos, setGMenuPos] = useState({ top: 0, left: 0 });

  // Load MB-wise Withdraw Groups (filtered by handler's assigned_mbs)
  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/coin-cut/withdraw-groups`, { params: { user_id: user.id } })
      .then(res => setWithdrawGroups(res.data?.groups || []))
      .catch(() => {});
  }, [user]);
  const [showBulkMB, setShowBulkMB] = useState(false);
  const [copyPopupChat, setCopyPopupChat] = useState(null);
  const [selectedChatId, setSelectedChatId] = useState(location.state?.selectedChatId || null);
  const chatListScrollRef = React.useRef(null);
  const scrollRestoredRef = React.useRef(false);

  // Auto-close the Customer Details 3rd panel whenever the active chat changes
  // or the handler switches panel (panel switches reset selectedChatId to null).
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('dws:close-customer-details'));
    // Expose the currently-open chat so InstantAccessG / group shortcuts (which
    // live outside this component and can't read its state) can carry it in
    // `fromState` → the group back-button re-opens THIS chat instead of closing
    // the right-side panel.
    try { sessionStorage.setItem('dws_open_chat_id', selectedChatId || ''); } catch (_) {}
  }, [selectedChatId]);


  // Feature 1: Preserve chat list scroll position across chat opens & reloads (WhatsApp-web style)
  const persistScroll = React.useCallback(() => {
    try {
      if (chatListScrollRef.current) sessionStorage.setItem('handler_chat_list_scroll', String(chatListScrollRef.current.scrollTop));
    } catch {}
  }, []);
  React.useEffect(() => {
    if (scrollRestoredRef.current) return;
    const el = chatListScrollRef.current;
    if (!el || (filteredChats?.length || 0) === 0) return;
    try {
      const saved = parseInt(sessionStorage.getItem('handler_chat_list_scroll') || '0', 10);
      if (saved > 0) {
        // Delay to let DOM layout finalize
        requestAnimationFrame(() => { el.scrollTop = saved; });
      }
      scrollRestoredRef.current = true;
    } catch {}
  }, [filteredChats?.length]);
  const [isDesktop, setIsDesktop] = useState(() => shouldRenderDesktop());
  // Web redesign (desktop) — resizable chat-list width, persisted per session.
  const [listWidth, setListWidth] = useState(() => {
    try { const v = parseInt(localStorage.getItem('dws_list_width'), 10); if (v >= 280 && v <= 680) return v; } catch (_) {}
    return 380;
  });
  const [showMbDropdown, setShowMbDropdown] = useState(false);
  const startListResize = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = listWidth;
    let latest = startW;
    const onMove = (ev) => { latest = Math.min(680, Math.max(280, startW + (ev.clientX - startX))); setListWidth(latest); };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      try { localStorage.setItem('dws_list_width', String(latest)); } catch (_) {}
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };
  // Session-41: manual refresh tick — bumping this restarts the loadChats +
  // loadUnreadCounts effect below (mirrors AdminDashboard's refresh button).
  const [refreshTick, setRefreshTick] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  // Update selectedChatId when navigating from ClientList
  useEffect(() => {
    if (location.state?.selectedChatId) {
      setSelectedChatId(location.state.selectedChatId);
    }
  }, [location.state]);

  // Detect desktop/mobile (disabled in PWA + touch + UA-mobile devices)
  useEffect(() => {
    const handleResize = () => setIsDesktop(shouldRenderDesktop());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const [unreadCounts, setUnreadCounts] = useState({}); // {room_id: count}
  
  // Selection and deletion states
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedRooms, setSelectedRooms] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showClientListModal, setShowClientListModal] = useState(false);
  // Session-41 CRITICAL FIX: `longPressTimer` and `touchStartPos` MUST be refs,
  // not React state. When they were `useState`, every mousedown fired a
  // parent re-render — which caused the react-window `rowComponent` (an
  // inline arrow function, new reference on every render) to be re-wrapped
  // via `React.memo` inside react-window → EVERY VISIBLE ROW REMOUNTED
  // between mousedown and mouseup. The browser fires a `click` event only
  // when mousedown+mouseup land on the same DOM node, so the click was
  // being dropped and the chat pane never opened. Reproduced on desktop
  // web view for deposit_handler switching between Deposit / Withdrawal
  // tabs. Refs give us the same behaviour without triggering re-renders.
  const longPressTimerRef = React.useRef(null);
  const touchStartPosRef = React.useRef(null);
  // Point 55 — handle to the document-level multi-touch guard registered
  // while a long-press timer is pending (see handleLongPressStart).
  const longPressGlobalGuardRef = React.useRef(null);
  
  // Deposit handler: chat type tab state (Deposit/Withdrawal/Support)
  const [chatTypeTab, setChatTypeTab] = useState(() => {
    try {
      const saved = sessionStorage.getItem('hdl_chat_type_tab');
      // Session-48h: added 'inquiry' as its own top-level tab (was sub-tab of support).
      if (saved && ['deposit', 'withdrawal', 'support', 'inquiry'].includes(saved)) return saved;
    } catch (_) {}
    return user?.chat_type || 'deposit';
  });
  // Batch H — keyboard nav (Ctrl+D/W/S/I): GlobalKeyboardShortcuts.jsx
  // navigates here with `state.chatTypeTab` when arriving from elsewhere
  // (handled by the existing location.state effect below); when already
  // sitting on this dashboard it instead dispatches this event so the tab
  // switches without a route change. Only meaningful for deposit_handler,
  // which is the only role with this multi-section tab switcher.
  useEffect(() => {
    const onJump = (e) => {
      if (isDepositHandler && ['deposit', 'withdrawal', 'support', 'inquiry'].includes(e.detail)) {
        setChatTypeTab(e.detail);
      }
    };
    window.addEventListener('dws:kbnav-jump-section', onJump);
    return () => window.removeEventListener('dws:kbnav-jump-section', onJump);
  }, [isDepositHandler]);

  // Batch H, point 3 — bare Ctrl / bare Tab cycles Deposit→Withdraw→
  // Support→Inquiry, only for deposit_handler (the only role with this
  // multi-section tab switcher) and only while sitting at one of those
  // main pages (no chat open).
  const onSectionCycle = React.useCallback((next) => setChatTypeTab(next), []);
  useSectionCycleKeys(
    MAIN_SECTIONS,
    chatTypeTab,
    onSectionCycle,
    isDepositHandler && !selectedChatId && MAIN_SECTIONS.includes(chatTypeTab)
  );

  // Session-48h: supportSubTab now only 'support' (inquiry moved to its own
  // top-level slide tab). Kept the state so any lingering references don't
  // crash — always coerces to 'support' now.
  const [supportSubTab, setSupportSubTab] = useState('support');
  // Cross-department unread counts for deposit handler tabs
  const [tabUnreadCounts, setTabUnreadCounts] = useState({ deposit: 0, withdrawal: 0, support: 0, inquiry: 0 });
  // Deposit handler: inquiry chats for settings
  const [inquiryChats, setInquiryChats] = useState([]);
  const [showInquiryInSettings, setShowInquiryInSettings] = useState(false);
  
  const menuRef = useRef(null);
  const prevUnreadCountsRef = useRef({});
  const isFirstLoadRef = useRef(true);
  const notifiedRoomsRef = useRef(new Set());
  // POINT 1 — the chat_type the user is CURRENTLY viewing. Used to discard
  // stale/out-of-order `/handler/chats` responses (from a tab already left)
  // so a broader/wrong list never overwrites the correct MB-filtered one.
  const activeChatTypeRef = useRef(null);
  // Issue 16 — group unread tracking (sound/notification + app badge).
  const prevGroupUnreadRef = useRef({});
  // Issue 16 — app badge = unread 1-on-1 chats + unread groups.
  useEffect(() => {
    const grpSrc = isThirdPartyHandler ? (withdrawGroups || []) : (groups || []);
    const oneOnOne = Object.values(unreadCounts).filter(c => c > 0).length;
    const grp = grpSrc.filter(g => (g.unread_count || 0) > 0).length;
    updateAppBadge(oneOnOne + grp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unreadCounts, groups, withdrawGroups, isThirdPartyHandler]);

  // Issue 16 — sound + notification when any group's unread count rises
  // (baseline-suppressed). Group chat opens on a separate /groups/:id page.
  useEffect(() => {
    const grpSrc = isThirdPartyHandler ? (withdrawGroups || []) : (groups || []);
    const cur = {};
    grpSrc.forEach(g => { cur[g.id] = g.unread_count || 0; });
    if (isFirstGroupLoadRef.current) {
      isFirstGroupLoadRef.current = false;
      prevGroupUnreadRef.current = cur;
      return;
    }
    const prev = prevGroupUnreadRef.current;
    grpSrc.forEach(g => {
      if ((g.unread_count || 0) > (prev[g.id] || 0)) {
        showNotification({
          title: g.name || 'Group',
          body: 'New group message',
          tag: `handler-group-${g.id}-${Date.now()}`,
          data: { groupId: g.id, isGroup: true },
        });
      }
    });
    prevGroupUnreadRef.current = cur;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, withdrawGroups, isThirdPartyHandler]);

  const isFirstGroupLoadRef = useRef(true);
  
  // Refresh function for manual refresh button
  const handleRefresh = async () => {
    if (!user) return;
    try {
      const chatType = isDepositHandler ? chatTypeTab : (user.chat_type || 'deposit');
      const response = await axios.get(`${API}/handler/chats`, {
        params: { handler_id: user.id, chat_type: chatType }
      });
      setChats(response.data);
      try { localStorage.setItem('hdl_chats', JSON.stringify(response.data)); } catch {}
      
      const groupsResponse = await axios.get(`${API}/groups/user-groups`, {
        params: { user_id: user.id }
      });
      setGroups(groupsResponse.data || []);
      try { localStorage.setItem('hdl_groups', JSON.stringify(groupsResponse.data || [])); } catch {}
      
      toast.success('Refreshed!');
    } catch (error) {
      console.error('Refresh failed:', error);
      toast.error('Refresh failed');
    }
  };
  
  // Load inquiry chats for deposit handler settings
  const loadInquiryChats = async () => {
    if (!user || !isDepositHandler) return;
    try {
      const response = await axios.get(`${API}/handler/chats`, {
        params: { handler_id: user.id, chat_type: 'inquiry' }
      });
      setInquiryChats(response.data || []);
    } catch (error) {
      console.error('Failed to load inquiry chats:', error);
    }
  };

  // Handle navigation state from CreateClient back button
  useEffect(() => {
    if (location.state?.bottomTab) {
      setBottomTab(location.state.bottomTab);
    }
    // Restore chat type tab for deposit handler
    if (location.state?.chatTypeTab && isDepositHandler) {
      setChatTypeTab(location.state.chatTypeTab);
    }
    // Clear the state to prevent it from persisting
    window.history.replaceState({}, document.title);
  }, [location]);

  // Persist chat type tab so mobile back-navigation lands on the same tab
  useEffect(() => {
    try { sessionStorage.setItem('hdl_chat_type_tab', chatTypeTab); } catch (_) {}
  }, [chatTypeTab]);

  // On chatTypeTab change, instantly swap in cached chats for that type so
  // there's no flash of stale (previous-tab) data while the fresh fetch runs.
  useEffect(() => {
    if (!isDepositHandler) return;
    let key = chatTypeTab;
    if (chatTypeTab === 'support') key = supportSubTab;
    const cached = chatsByTypeRef.current[key];
    if (Array.isArray(cached)) setChats(cached);
  }, [chatTypeTab, supportSubTab, isDepositHandler]);

  // Session-53 touch-fix — prefetch OTHER chat types ONLY when tab actually
  // changes (was firing on every 3s poll cycle earlier, which caused
  // filteredChats churn → accidental long-press during side-swipe).
  useEffect(() => {
    if (!isDepositHandler || !user?.id) return;
    const currentType = chatTypeTab === 'inquiry' ? 'inquiry'
                       : chatTypeTab === 'support' ? 'support'
                       : chatTypeTab;
    const others = ['deposit', 'withdrawal', 'support', 'inquiry'].filter(t => t !== currentType);
    let cancelled = false;
    (async () => {
      try {
        const results = await Promise.all(others.map(t =>
          axios.get(`${API}/handler/chats`, { params: { handler_id: user.id, chat_type: t } })
            .then(r => ({ type: t, data: r.data }))
            .catch(() => null)
        ));
        if (cancelled) return;
        results.forEach(r => {
          if (r && Array.isArray(r.data)) chatsByTypeRef.current[r.type] = r.data;
        });
        try { localStorage.setItem('hdl_chats_by_type', JSON.stringify(chatsByTypeRef.current)); } catch {}
      } catch (_) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [chatTypeTab, isDepositHandler, user?.id]);

  // Persist filter tab (All / Unread / Favourites / Groups / Archive) across back-nav
  useEffect(() => {
    try { sessionStorage.setItem('hdl_active_tab', activeTab); } catch (_) {}
  }, [activeTab]);

  // Real-time cross-user unread sync
  useEffect(() => {
    if (!user?.id) return;
    const handler = (payload) => {
      const rid = payload?.room_id;
      if (!rid) return;
      setUnreadCounts(prev => (prev[rid] ? { ...prev, [rid]: 0 } : prev));
      setChats(prev => prev.map(c => c.room_id === rid ? { ...c, unread_count: 0 } : c));
    };
    onMessagesRead(handler);
    return () => offMessagesRead(handler);
  }, [user?.id]);
  
  // ========== ONLINE STATUS HEARTBEAT ==========
  // Mark handler as online when on dashboard (critical for delivery status)
  useEffect(() => {
    if (!user?.id) return;
    
    // Connect socket for real-time calls & notifications
    connectSocket(user.id);
    
    // Set online immediately when dashboard loads
    axios.post(`${API}/users/online-status?user_id=${user.id}`, {
      is_online: true
    }).catch(err => console.error('❌ Online status update failed:', err));
    
    // Slow heartbeat every 30s — socket presence (register_user + disconnect
    // grace) is now the primary source of truth for online/offline.
    const heartbeatInterval = setInterval(() => {
      axios.post(`${API}/users/online-status?user_id=${user.id}`, {
        is_online: true
      }).catch(err => console.error('❌ Heartbeat failed:', err));
    }, 30000);
    
    // On unmount we no longer flip is_online=false — socket disconnect handler
    // owns that via a 15s grace period, avoiding flicker on route changes.
    return () => {
      clearInterval(heartbeatInterval);
    };
  }, [user?.id]);

  // Cross-department notifications + LIVE chat list sync via Socket.IO
  // Every incoming socket message immediately bumps the affected chat's
  // last_message + last_message_time in local state so the sort reruns and
  // the row jumps to the top — no need to leave the page and come back.
  const notifiedMsgIds = useRef(new Set());
  // Mirror selectedChatId so the socket callback can read the latest value
  // without needing to re-register on every selection change.
  const selectedChatIdRef = useRef(selectedChatId);
  useEffect(() => { selectedChatIdRef.current = selectedChatId; }, [selectedChatId]);
  useEffect(() => {
    if (!user?.id) return;

    const previewFor = (msg) => {
      if (!msg) return '';
      if (msg.is_sticker) return '🎨 Sticker';
      if (msg.voice_url) return '🎤 Voice message';
      if (msg.image_url) return '📷 Photo';
      if (msg.document_url) return '📄 ' + (msg.document_name || 'Document');
      if (msg.call_info) return '📞 Voice call';
      return (msg.text || '').slice(0, 40);
    };

    const handleNewMessage = async (data) => {
      if (!data?.message || !data.room_id) return;
      const msg = data.message;
      const roomId = data.room_id;

      // 1) LIVE chat list update — instant reorder + preview refresh
      setChats(prev => {
        let touched = false;
        const next = prev.map(c => {
          if (c.room_id !== roomId) return c;
          touched = true;
          return {
            ...c,
            last_message: previewFor(msg),
            last_message_time: msg.timestamp || new Date().toISOString(),
            last_message_sender_id: msg.sender_id,
            last_message_sender_role: msg.sender_role,
            // Also bump unread if the message is from someone else and this
            // chat isn't currently selected (desktop) / open (mobile).
            unread_count: (msg.sender_id !== user.id && selectedChatIdRef.current !== roomId)
              ? (c.unread_count || 0) + 1
              : c.unread_count,
          };
        });
        return touched ? next : prev;
      });

      // 2) Cross-department notifications + tab unread counts (deposit handler only)
      if (isDepositHandler && msg.sender_id !== user.id) {
        // Dedup: don't notify for same message twice
        if (notifiedMsgIds.current.has(msg.id)) return;
        notifiedMsgIds.current.add(msg.id);
        if (notifiedMsgIds.current.size > 200) {
          const arr = [...notifiedMsgIds.current];
          notifiedMsgIds.current = new Set(arr.slice(-100));
        }
        try {
          const roomRes = await axios.get(`${API}/chat/room/${roomId}`);
          const chatType = roomRes.data?.chat_type;
          // Point 32 — this socket path and the `loadUnreadCounts` poll below
          // both watch the handler's own department and could otherwise both
          // fire a push notification for the same incoming message. They now
          // share `notifiedRoomsRef` as one cooldown gate per room so only
          // whichever one sees the message first actually notifies.
          if (
            chatType === (user.chat_type || 'deposit') &&
            !notifiedRoomsRef.current.has(roomId) &&
            roomId !== selectedChatIdRef.current
          ) {
            showNotification({
              title: `New message - ${msg.sender_name || 'Customer'}`,
              body: previewFor(msg) || 'New message',
              tag: `handler-${roomId}`,
              data: { roomId, chatType }
            });
            notifiedRoomsRef.current.add(roomId);
            setTimeout(() => { notifiedRoomsRef.current.delete(roomId); }, 5000);
          }
          setTabUnreadCounts(prev => ({
            ...prev,
            [chatType]: (prev[chatType] || 0) + 1
          }));
        } catch (e) {}
      }
    };

    onNewMessage(handleNewMessage);
    return () => offNewMessage(handleNewMessage);
  }, [user?.id, isDepositHandler]);

  // Fetch cross-department unread counts for tab badges
  useEffect(() => {
    if (!user?.id || !isDepositHandler) return;
    
    const fetchTabCounts = async () => {
      try {
        const counts = { deposit: 0, withdrawal: 0, support: 0, inquiry: 0 };
        const types = ['deposit', 'withdrawal', 'support', 'inquiry'];
        const results = await Promise.all(types.map(type =>
          axios.get(`${API}/chat/unread-count`, { params: { user_id: user.id, chat_type: type } })
            .then(res => res.data.unread_count || 0)
            .catch(() => counts[type])
        ));
        types.forEach((t, i) => { counts[t] = results[i]; });
        setTabUnreadCounts(counts);
      } catch (e) {}
    };
    
    fetchTabCounts();
    const stopPoll = startPolling('handler-tab-counts', fetchTabCounts, 15000, { skipWhenHidden: true });
    return () => stopPoll();
  }, [user?.id, isDepositHandler]);

  // P18/P19 — realtime SR badges. Backend emits service_request_created/
  // _closed/_cancelled/_accepted; refresh the chat list (pending-SR pill 🎫)
  // and tab counts the instant one fires instead of waiting for the 15s poll
  // (was the 5-10 min stuck-count). Debounced so a burst collapses to one load.
  useEffect(() => {
    if (!user?.id) return;
    let h = null;
    const onSR = () => {
      if (h) clearTimeout(h);
      h = setTimeout(() => setRefreshTick(t => t + 1), 350);
    };
    socket.on('service_request_created', onSR);
    socket.on('service_request_closed', onSR);
    socket.on('service_request_cancelled', onSR);
    socket.on('service_request_accepted', onSR);
    return () => {
      if (h) clearTimeout(h);
      socket.off('service_request_created', onSR);
      socket.off('service_request_closed', onSR);
      socket.off('service_request_cancelled', onSR);
      socket.off('service_request_accepted', onSR);
    };
  }, [user?.id]);

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showMenu]);

  // Load chats
  useEffect(() => {
    if (!user) return;

    // POINT 1 — record the chat_type of the CURRENTLY-selected tab so that any
    // late/out-of-order response can be validated against it before it touches
    // state. Computed identically to `chatType` inside loadChats below.
    let _expectedType = isDepositHandler ? chatTypeTab : (user.chat_type || 'deposit');
    if (isDepositHandler && chatTypeTab === 'inquiry') _expectedType = 'inquiry';
    else if (isDepositHandler && chatTypeTab === 'support') _expectedType = supportSubTab;
    activeChatTypeRef.current = _expectedType;

    const loadChats = async () => {
      try {
        let chatType = isDepositHandler ? chatTypeTab : (user.chat_type || 'deposit');
        // Session-48h: inquiry is now its own top-level tab. When on inquiry
        // we ALWAYS pull `chat_type=inquiry` chats regardless of subTab.
        if (isDepositHandler && chatTypeTab === 'inquiry') {
          chatType = 'inquiry';
        } else if (isDepositHandler && chatTypeTab === 'support') {
          chatType = supportSubTab; // 'support' or 'inquiry'
        }
        // FIX 2 — is this a background refresh (we already hold this type's
        // chats) or a first load? On refresh we must NEVER paint a partial page
        // (that shrank the list and caused the scroll jump); the COMPLETE list
        // is applied once at the end instead.
        const isRefresh = Array.isArray(chatsByTypeRef.current[chatType]) && chatsByTypeRef.current[chatType].length > 0;
        const PAGE = 500;
        const first = await axios.get(`${API}/handler/chats`, {
          params: {
            handler_id: user.id,
            chat_type: chatType,
            limit: PAGE,
            offset: 0
          }
        });

        // POINT 1 — discard stale/out-of-order responses. If the user switched
        // tabs while this request was in flight, `chatType` no longer matches
        // the currently-selected tab; applying it would show the wrong (often
        // broader, cross-MB) list. Silently drop it — the current tab's own
        // request/poll will paint the correct data.
        if (chatType !== activeChatTypeRef.current) return;

        let acc = Array.isArray(first.data) ? first.data : (first.data.rooms || []);
        let hasMore = Array.isArray(first.data) ? false : !!first.data.has_more;
        const applyChats = (list) => {
          setChats(prev => {
            if (JSON.stringify(prev) === JSON.stringify(list)) return prev;
            try { localStorage.setItem('hdl_chats', JSON.stringify(list)); } catch {}
            // Cache by chatType so future tab-switches render instantly
            try {
              chatsByTypeRef.current[chatType] = list;
              localStorage.setItem('hdl_chats_by_type', JSON.stringify(chatsByTypeRef.current));
            } catch {}
            return list;
          });
        };
        if (!isRefresh) applyChats(acc);
        
        // Also load groups
        const groupsResponse = await axios.get(`${API}/groups/user-groups`, {
          params: { user_id: user.id }
        });
        setGroups(prev => {
          const newData = groupsResponse.data || [];
          if (JSON.stringify(prev) === JSON.stringify(newData)) return prev;
          try { localStorage.setItem('hdl_groups', JSON.stringify(newData)); } catch {}
          return newData;
        });
        
        setLoading(false);

        // Stream the REMAINING chats in 50-chunks in the background so the old
        // hard cap is gone and the first paint isn't blocked. Aborts if the
        // user switches tab mid-load. All chats still land client-side so
        // search / filters / counts stay accurate.
        let offset = acc.length;
        let guard = 0;
        while (hasMore && guard < 500 && chatType === activeChatTypeRef.current) {
          guard += 1;
          let res;
          try {
            res = await axios.get(`${API}/handler/chats`, {
              params: { handler_id: user.id, chat_type: chatType, limit: PAGE, offset }
            });
          } catch (_) { break; }
          if (chatType !== activeChatTypeRef.current) return;
          const chunk = Array.isArray(res.data) ? res.data : (res.data.rooms || []);
          if (!chunk.length) break;
          const seen = new Set(acc.map(c => c.room_id));
          acc = acc.concat(chunk.filter(c => !seen.has(c.room_id)));
          hasMore = Array.isArray(res.data) ? false : !!res.data.has_more;
          offset += chunk.length;
          if (!isRefresh) applyChats(acc);
        }
        // FIX 2 — apply the COMPLETE list once (refresh never shows a partial
        // page; first-load already painted progressively above).
        if (chatType === activeChatTypeRef.current) applyChats(acc);

        // Session-53 (touch-oversensitivity fix) — prefetch of the OTHER
        // chat types moved OUT of the 3s poll cycle. It now runs only when
        // `chatTypeTab` actually changes (see `prefetchOtherChatTypes`
        // useEffect below), so every 3-second heartbeat only refreshes the
        // currently-visible tab. Fewer background axios rounds → fewer
        // filteredChats churn cycles → fewer accidental long-press
        // registrations while user swipes.
      } catch (error) {
        console.error('Failed to load chats:', error);
        setLoading(false);
        // One silent retry — dead TCP sockets after backgrounding are the
        // most common cause and the second request usually succeeds on a
        // fresh connection. Only toast when we don't already have cached
        // data (otherwise the stale data is still visible and the periodic
        // poll will heal in the background).
        if (!loadChats._retried) {
          loadChats._retried = true;
          setTimeout(() => { loadChats._retried = false; loadChats(); }, 800);
          return;
        }
        try {
          const hasCache = localStorage.getItem('hdl_chats');
          if (!hasCache) toast.error('Could not load chats — check network');
        } catch {}
      }
    };

    const loadUnreadCounts = async () => {
      try {
        const response = await axios.get(`${API}/chat/unread-count`, {
          params: { user_id: user.id }
        });
        
        // Convert array to object: {room_id: count}
        const countsMap = {};
        if (response.data.unread_rooms) {
          response.data.unread_rooms.forEach(item => {
            countsMap[item.room_id] = item.count;
          });
        }
        
        // ========== NOTIFICATION LOGIC ==========
        // Skip first load to prevent notification replay on navigation back
        if (isFirstLoadRef.current) {
          isFirstLoadRef.current = false;
        } else {
          const prevCounts = prevUnreadCountsRef.current;
          
          Object.keys(countsMap).forEach(roomId => {
            const currentCount = countsMap[roomId];
            const prevCount = prevCounts[roomId] || 0;
            
            // New unread message detected - but not if we're actively viewing that chat
            if (currentCount > prevCount && !notifiedRoomsRef.current.has(roomId) && roomId !== selectedChatId) {
              // Find chat details for better notification
              const chat = chats.find(c => c.id === roomId);
              const customerName = chat?.customer_name || chat?.customer?.username || 'Customer';
              const chatType = chat?.chat_type || 'chat';
              
              // Show notification with sound
              showNotification({
                title: `${customerName} - ${chatType.toUpperCase()}`,
                body: (typeof chat?.last_message === 'string' ? chat.last_message : (chat?.last_message?.text || chat?.last_message_text)) || 'New message',
                tag: `handler-dashboard-${roomId}`,
                data: { roomId, chatType }
              });
              
              // Mark this room as notified
              notifiedRoomsRef.current.add(roomId);
              
              // Remove from notified set after 5 seconds so subsequent messages can trigger again
              setTimeout(() => {
                notifiedRoomsRef.current.delete(roomId);
              }, 5000);
            }
          });
        }
        
        // Update prev counts ref
        prevUnreadCountsRef.current = countsMap;
        
        // Smart setState - only re-render if counts actually changed
        setUnreadCounts(prev => {
          if (JSON.stringify(prev) === JSON.stringify(countsMap)) return prev;
          return countsMap;
        });
        // Issue 16 — app badge (1-on-1 + groups) is set in a dedicated effect.
      } catch (error) {
        console.error('Failed to load unread counts:', error);
      }
    };

    loadChats();
    loadUnreadCounts();
    
    // Background-safe polling using Web Worker.
    // Phase B (real-time dashboards) — the socket `new_message` + `messages_read`
    // handlers update chats & unread counts INSTANTLY; this poll is now just a
    // safety-net for new rooms / dropped events. Slowed 3s → 15s to cut DB load
    // ~5x (onVisibilityFetch still refetches on tab focus).
    const stopPoll = startPolling('handler-dashboard', () => {
      loadChats();
      loadUnreadCounts();
    }, 15000, { skipWhenHidden: true });
    const cleanupVis = onVisibilityFetch(() => {
      loadChats();
      loadUnreadCounts();
    });
    return () => { stopPoll(); cleanupVis(); };
  }, [user, chatTypeTab, supportSubTab, refreshTick]);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Filter chats based on search and active tab
  useEffect(() => {
    let filtered = [...chats];

    // Search filter (using debounced query)
    if (debouncedSearchQuery) {
      if (activeTab === 'groups') {
        const src = isThirdPartyHandler ? (withdrawGroups || []) : groups;
        filtered = src.filter(group =>
          group.name?.toLowerCase().includes(debouncedSearchQuery.toLowerCase())
        );
      } else {
        const qLower = debouncedSearchQuery.toLowerCase();
        // Session-48j: also match Client Code (serial_number). Handles both
        // exact and zero-padded matches so `10` finds SN `0010` too.
        const codeQuery = debouncedSearchQuery.replace(/^0+/, '');
        filtered = filtered.filter(chat => {
          const c = chat.customer || {};
          const a = c.client_address || {};
          const clientCode = String(c.serial_number || '').replace(/^0+/, '');
          return (
            (c.name || '').toLowerCase().includes(qLower) ||
            (c.phone || '').includes(debouncedSearchQuery) ||
            (c.ifsc_code || '').toLowerCase().includes(qLower) ||
            (c.client_id || '').toLowerCase().includes(qLower) ||
            (codeQuery && clientCode.startsWith(codeQuery)) ||
            (a.ifsc || '').toLowerCase().includes(qLower) ||
            (a.city || '').toLowerCase().includes(qLower) ||
            (a.state || '').toLowerCase().includes(qLower) ||
            (a.district || '').toLowerCase().includes(qLower) ||
            (a.branch || '').toLowerCase().includes(qLower) ||
            (a.bank || '').toLowerCase().includes(qLower)
          );
        });
        // Apply tab filter after search
        if (activeTab === 'unread') {
          filtered = filtered.filter(chat => chat.unread_count > 0);
        } else if (activeTab === 'favourites') {
          filtered = filtered.filter(chat => chat.is_favourite === true);
        }
      }
    } else {
      // No search - apply tab filter only
      if (activeTab === 'unread') {
        filtered = filtered.filter(chat => chat.unread_count > 0 && !chat.is_archived);
      } else if (activeTab === 'favourites') {
        filtered = chats.filter(chat => chat.is_favourite === true && !chat.is_archived);
      } else if (activeTab === 'groups') {
        // Session-40 (Task 2 Item 4b): show ONLY user-created groups here.
        // Withdraw system groups (system_key starting with `withdraw_group`)
        // are hidden — those live in their own separate section. Third-party
        // withdraw handlers still see their withdraw groups because that's
        // their only accessible surface.
        const raw = isThirdPartyHandler ? (withdrawGroups || []) : groups;
        filtered = isThirdPartyHandler
          ? raw
          : (raw || []).filter(g => !String(g?.system_key || '').startsWith('withdraw_group'));
      } else if (activeTab === 'archive') {
        filtered = chats.filter(chat => chat.is_archived === true);
      } else {
        // 'all' tab — hide archived chats from main list
        filtered = filtered.filter(chat => !chat.is_archived);
      }
    }

    // MB Setup filter — applies only for withdrawal view (own withdrawal handler OR deposit handler viewing withdrawal tab)
    // MB Setup filter — applies in deposit / withdrawal / support views
    const showMBTabsFilter = !isInquiryHandler && (
      ['deposit', 'withdrawal', 'support'].includes(user?.chat_type || '') ||
      (isDepositHandler && ['deposit', 'withdrawal', 'support'].includes(chatTypeTab))
    );
    if (showMBTabsFilter && activeTab !== 'groups' && mbFilter !== 'all') {
      filtered = filtered.filter(chat => chat.customer?.mb_setup === mbFilter);
    }

    // Feature 2: Auto-enforce assigned_mbs (if handler has restricted MBs)
    const assignedMbs = Array.isArray(user?.assigned_mbs) ? user.assigned_mbs : [];
    if (assignedMbs.length > 0 && activeTab !== 'groups') {
      filtered = filtered.filter(chat => {
        const mb = chat.customer?.mb_setup;
        return mb && assignedMbs.includes(mb);
      });
    }

    // WhatsApp-style sort: newest activity on top. Handles both chats (using
    // `last_message?.timestamp` or the top-level `updated_at`) and groups
    // (`last_message_at` / `updated_at`). Legacy rows missing the `Z` timezone
    // marker are normalised so parsing stays deterministic.
    const parseTs = (item) => {
      const raw = item.last_message?.timestamp
                || item.last_message_time
                || item.last_message_at
                || item.updated_at
                || item.created_at
                || '';
      if (!raw) return 0;
      const norm = typeof raw === 'string' && !/[Zz]|[+-]\d\d:?\d\d$/.test(raw) ? raw + 'Z' : raw;
      const t = new Date(norm).getTime();
      return isNaN(t) ? 0 : t;
    };
    filtered.sort((a, b) => parseTs(b) - parseTs(a));
    // Session-53 touch-fix — deep-compare guard. Only fire setFilteredChats
    // when the resulting array is actually different from current state.
    // .filter() + .sort() always return NEW array references; skipping the
    // setter when data is identical prevents unnecessary row re-renders
    // during 3s poll cycles that were interrupting user swipe gestures
    // and triggering accidental long-press selection mode.
    try {
      if (JSON.stringify(filtered) === JSON.stringify(filteredChats)) {
        return;
      }
    } catch (_) { /* fall through to setFilteredChats */ }
    setFilteredChats(filtered);
  }, [chats, groups, withdrawGroups, isThirdPartyHandler, debouncedSearchQuery, activeTab, mbFilter, chatTypeTab, isDepositHandler, isInquiryHandler, user?.chat_type, user?.assigned_mbs]);

  // Reset the progressive-render window to 30 ONLY when the user actually
  // changes a filter / tab / search — never on background data or socket
  // updates. Previously this lived inside the sort/filter effect above, so it
  // fired on every `chats` change: the slow-DB 50-chunk background stream and
  // each incoming socket message collapsed the list back to 30 rows mid-scroll,
  // making it jump to the top and sometimes show only a couple of chats.
  useEffect(() => {
    setVisibleCount(30);
  }, [debouncedSearchQuery, activeTab, mbFilter, chatTypeTab]);

  // Progressive-render observer for the handler chat list
  const chatListLoadMoreRef = useRef(null);
  useEffect(() => {
    const el = chatListLoadMoreRef.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some(e => e.isIntersecting)) {
        setVisibleCount(c => Math.min(c + 30, filteredChats.length));
      }
    }, { root: null, rootMargin: '200px', threshold: 0 });
    io.observe(el);
    return () => io.disconnect();
  }, [filteredChats.length, visibleCount]);

  const handleChatClick = (chat) => {
    const rid = chat.room_id;
    // Optimistic: clear unread badge instantly so no stale count flashes on back-nav
    setUnreadCounts(prev => (prev[rid] ? { ...prev, [rid]: 0 } : prev));
    setChats(prev => prev.map(c => c.room_id === rid ? { ...c, unread_count: 0 } : c));
    // Instant (WhatsApp-like): also drop this chat from its DEPARTMENT tab badge
    // right away, instead of waiting for the 5s cross-department count poll or a
    // manual refresh. tabUnreadCounts counts unread CHATS per type, so decrement
    // by one only when this chat actually had unread messages.
    if ((chat.unread_count || 0) > 0) {
      const _type = chat.chat_type || (chat.customer && chat.customer.chat_type) || chatTypeTab;
      setTabUnreadCounts(prev => {
        const cur = prev[_type] || 0;
        return cur > 0 ? { ...prev, [_type]: cur - 1 } : prev;
      });
    }
    if (isDesktop) {
      setSelectedChatId(rid);
    } else {
      // Stamp current tab into the dashboard history entry BEFORE
      // pushing the chat route so back-nav restores the correct tab.
      navigate(location.pathname, { replace: true, state: { ...(location.state || {}), chatTypeTab } });
      navigate(`/handler-chat/${rid}`, { state: { chatTypeTab: chatTypeTab } });
    }
  };

  const handleGroupClick = (groupId) => {
    // Carry `from` + `fromState` so GroupChatPage's back button returns to THIS
    // dashboard AND re-opens the 1-on-1 chat that was open (selectedChatId),
    // instead of dropping to the groups list.
    const from = window.location.pathname + window.location.search + window.location.hash;
    navigate(location.pathname, { replace: true, state: { ...(location.state || {}), chatTypeTab } });
    navigate(`/groups/${groupId}`, { state: { from, fromState: { selectedChatId, chatTypeTab } } });
  };

  const toggleFavourite = async (roomId, currentStatus) => {
    try {
      await axios.post(`${API}/chat/rooms/${roomId}/favourite`, {
        user_id: user.id
      });
      
      // Update local state
      setChats(prevChats => 
        prevChats.map(chat => 
          chat.room_id === roomId 
            ? { ...chat, is_favourite: !currentStatus }
            : chat
        )
      );
      
      toast.success(currentStatus ? 'Removed from favourites' : 'Added to favourites');
    } catch (error) {
      console.error('Toggle favourite error:', error);
      toast.error('Failed to update favourite');
    }
  };

  // Copy client bank details
  const copyClientDetails = async (chat) => {
    try {
      // Fetch client full details
      const response = await axios.get(`${API}/clients/registered`);
      const customerId = chat.customer_id || chat.customer?.id;
      const client = response.data.find(c => 
        c.id === customerId || 
        c.phone === chat.customer_phone ||
        c.phone === chat.customer?.phone
      );
      
      if (!client) {
        toast.error('Client details not found');
        return;
      }
      
      // Format: Username, A/c Holder, Bank, IFSC, Bank Name, Address, UPI
      let formattedText = `User Name: ${client.username || 'N/A'}

A/c Holder Name: ${client.account_holder_name || client.client_name || client.username || 'N/A'}

Bank Account No: ${client.bank_account_number || 'N/A'}

IFSC Code: ${client.ifsc_code || 'N/A'}

Bank Name: ${client.bank_name || 'N/A'}`;

      if (client.address) {
        formattedText += `\n\nAddress: ${client.address}`;
      }
      if (client.upi_id) {
        formattedText += `\n\nUPI ID: ${client.upi_id}`;
      }

      // Fetch dynamic customer login link from settings
      try {
        const settingsRes = await axios.get(`${API}/settings`);
        const loginLink = settingsRes.data?.customer_login_link;
        if (loginLink) {
          formattedText += `\nLogin Link: ${loginLink}`;
        }
      } catch (e) { /* skip if settings fetch fails */ }

      await navigator.clipboard.writeText(formattedText);
      toast.success('Details copied with login link!');
    } catch (error) {
      console.error('Copy error:', error);
      toast.error('Failed to copy details');
    }
  };

  // Open WhatsApp with client number (PWA-compatible)
  const openWhatsApp = (phone, roomId) => {
    if (!phone) {
      toast.error('No phone number available');
      return;
    }
    // Staff-only audit note in the chat (customer never sees it) — logs which
    // handler/admin redirected to the customer's WhatsApp. Fire-and-forget.
    if (roomId) {
      axios.post(`${API}/chat/room/${roomId}/call-log`, { user_id: user?.id, kind: 'whatsapp' }).catch(() => {});
    }
    // Remove any non-numeric characters and add country code if needed
    const cleanNumber = phone.replace(/\D/g, '');
    const whatsappNumber = cleanNumber.startsWith('91') ? cleanNumber : `91${cleanNumber}`;
    // Use anchor click for PWA compatibility
    const link = document.createElement('a');
    link.href = `https://wa.me/${whatsappNumber}`;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleLogout = () => {
    logout(); // logout() handles redirect automatically
  };

  // WhatsApp-style chat-list time — imported from shared util so admin,
  // handler + mobile views all render identically. See utils/formatChatTime.js.
  const formatTime = formatChatTime;

  // Long-press handlers for selection mode
  const handleLongPressStart = (roomId, e) => {
    // Multi-touch guard — a 3-finger screenshot gesture briefly fires
    // touchstart on the item under one of the fingers. If more than one
    // finger is down, do not arm the long-press timer at all.
    if (e.touches && e.touches.length > 1) return;

    // Do NOT preventDefault here — that blocks the browser's native
    // click/scroll handling and can even trigger on synthetic touch events
    // fired during 3-finger screenshot gestures on Android.
    // Get touch/mouse position
    const pos = {
      x: e.touches ? e.touches[0].clientX : e.clientX,
      y: e.touches ? e.touches[0].clientY : e.clientY
    };
    touchStartPosRef.current = pos;

    // Session-38 perf: piggyback prefetch on touchstart / mousedown. Race
    // starts here so by the time the click fires and the chat view mounts,
    // the messages payload is often already in the browser cache. Zero risk
    // — errors are swallowed inside the prefetch helper.
    try {
      if (!selectionMode && roomId && user?.id) {
        prefetchRoom(roomId, () => axios.get(`${API}/chat/messages/${roomId}`, {
          params: { user_id: user.id, limit: 50 }
        }).then(r => r.data));
      }
    } catch (_) { /* silent */ }

    // 1500ms — longer than WhatsApp default so a 3-finger screenshot gesture
    // (which briefly synthesises a touchstart) doesn't trip selection mode.
    const timer = setTimeout(() => {
      setSelectionMode(true);
      setSelectedRooms([roomId]);
      clearLongPressGlobalGuard();
    }, 1500);
    longPressTimerRef.current = timer;

    // Point 55 — the per-row touchstart/touchmove checks only see a finger
    // landing/moving on THIS row's own element. A real 3-finger screenshot
    // gesture usually lands its 2nd/3rd finger elsewhere on the screen, not
    // on the same row, so those checks can miss it if the first finger stays
    // still. Catch that case with a document-level listener that fires the
    // instant ANY 2nd touch appears anywhere, regardless of target.
    if (e.touches) {
      const guard = (ev) => {
        if (ev.touches && ev.touches.length > 1) {
          if (longPressTimerRef.current) { clearTimeout(longPressTimerRef.current); longPressTimerRef.current = null; }
          touchStartPosRef.current = null;
          clearLongPressGlobalGuard();
        }
      };
      longPressGlobalGuardRef.current = guard;
      document.addEventListener('touchstart', guard, { passive: true });
    }
  };

  const clearLongPressGlobalGuard = () => {
    if (longPressGlobalGuardRef.current) {
      document.removeEventListener('touchstart', longPressGlobalGuardRef.current);
      longPressGlobalGuardRef.current = null;
    }
  };

  const handleLongPressEnd = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    touchStartPosRef.current = null;
    clearLongPressGlobalGuard();
  };

  const handleTouchMove = (e) => {
    // Multi-touch that arrived AFTER the initial finger (e.g. the 2nd/3rd
    // finger of an Android screenshot gesture) — cancel immediately.
    if (e.touches && e.touches.length > 1) {
      if (longPressTimerRef.current) { clearTimeout(longPressTimerRef.current); longPressTimerRef.current = null; }
      touchStartPosRef.current = null;
      clearLongPressGlobalGuard();
      return;
    }
    if (!touchStartPosRef.current || !longPressTimerRef.current) return;
    
    // Get current position
    const currentPos = {
      x: e.touches ? e.touches[0].clientX : e.clientX,
      y: e.touches ? e.touches[0].clientY : e.clientY
    };
    
    // Calculate distance moved
    const distance = Math.sqrt(
      Math.pow(currentPos.x - touchStartPosRef.current.x, 2) + 
      Math.pow(currentPos.y - touchStartPosRef.current.y, 2)
    );
    
    // Only cancel if moved more than 15px (tolerant of finger jiggle + more
    // forgiving during multi-finger gestures like the Android screenshot).
    if (distance > 15) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
      touchStartPosRef.current = null;
      clearLongPressGlobalGuard();
    }
  };

  const toggleRoomSelection = (roomId) => {
    if (selectedRooms.includes(roomId)) {
      setSelectedRooms(prev => prev.filter(id => id !== roomId));
    } else {
      setSelectedRooms(prev => [...prev, roomId]);
    }
  };

  const handleDeleteRooms = async () => {
    // Handlers cannot permanently delete a chat — backend enforces super_admin only.
    // Instead the request is queued to admin's Settings → Permissions → Delete
    // Permissions where they approve/reject with full context (chat IDs + names).
    try {
      // Build a descriptive reason that includes the chat IDs + names so the
      // admin can identify each chat in the permissions panel.
      const names = selectedRooms.map(rid => {
        const c = chats.find(cc => cc.room_id === rid);
        return c ? (c.customer?.name || c.customer?.phone || rid.slice(0, 8)) : rid.slice(0, 8);
      });
      const reason = `Delete request for ${selectedRooms.length} chat(s): ${names.join(', ')}`;
      await axios.post(`${API}/delete-permission/request`, {
        handler_id: user.id,
        reason,
        target_room_ids: selectedRooms,
      });
      toast.success('Delete request sent to admin for approval');
    } catch (e) {
      console.error('Failed to send delete request:', e);
      toast.error('Failed to send request. Please try again.');
    }
    setShowDeleteConfirm(false);
    setSelectionMode(false);
    setSelectedRooms([]);
  };

  const handleToggleArchive = async () => {
    try {
      const isUnarchiving = activeTab === 'archive';
      for (const roomId of selectedRooms) {
        await axios.post(`${API}/chat/rooms/${roomId}/archive`, null, {
          params: { user_id: user.id, unarchive: isUnarchiving }
        });
      }
      toast.success(isUnarchiving ? 'Chats unarchived' : 'Chats archived');
      // Update local state — mark rooms as archived / unarchived
      setChats(prev => prev.map(c =>
        selectedRooms.includes(c.room_id) ? { ...c, is_archived: !isUnarchiving } : c
      ));
      setSelectionMode(false);
      setSelectedRooms([]);
    } catch (error) {
      console.error('Failed to toggle archive:', error);
      toast.error('Failed to archive chats');
    }
  };

  const handleTogglePinnedChat = async () => {
    try {
      // Point 53 — if every selected chat is already pinned, this unpins
      // them; otherwise it pins the ones that aren't (matches WhatsApp's
      // "pin selected" behavior for a mixed selection).
      const allPinned = selectedRooms.every(rid => chats.find(c => c.room_id === rid)?.is_pinned_chat);
      const results = await Promise.all(selectedRooms.map(async (roomId) => {
        const isPinned = chats.find(c => c.room_id === roomId)?.is_pinned_chat;
        if (allPinned === isPinned) {
          const res = await axios.post(`${API}/chat/rooms/${roomId}/pin-chat`, {});
          return { roomId, is_pinned_chat: res.data?.is_pinned_chat };
        }
        return null;
      }));
      const changed = new Map(results.filter(Boolean).map(r => [r.roomId, r.is_pinned_chat]));
      setChats(prev => prev.map(c => changed.has(c.room_id) ? { ...c, is_pinned_chat: changed.get(c.room_id) } : c));
      toast.success(allPinned ? 'Unpinned' : 'Pinned to top');
      setSelectionMode(false);
      setSelectedRooms([]);
    } catch (error) {
      console.error('Failed to toggle pin:', error);
      toast.error('Failed to update pin');
    }
  };

  const cancelSelection = () => {
    setSelectionMode(false);
    setSelectedRooms([]);
  };

  const handleAddToClientList = async () => {
    try {
      let successCount = 0;
      let errorCount = 0;
      let alreadyExistsCount = 0;
      
      for (const roomId of selectedRooms) {
        try {
          const response = await axios.post(`${API}/chat/room/add-to-client-list`, {
            room_id: roomId,
            user_id: user.id
          });
          
          if (response.data.message === "Already in client list") {
            alreadyExistsCount++;
          } else {
            successCount++;
          }
        } catch (err) {
          console.error(`Failed to add room ${roomId}:`, err);
          errorCount++;
        }
      }
      
      // Show appropriate toast messages
      if (successCount > 0) {
        toast.success(`${successCount} chat${successCount > 1 ? 's' : ''} added to client list!`);
      }
      
      if (alreadyExistsCount > 0) {
        toast.info(`${alreadyExistsCount} chat${alreadyExistsCount > 1 ? 's are' : ' is'} already in client list`);
      }
      
      if (errorCount > 0) {
        toast.error(`Failed to add ${errorCount} chat${errorCount > 1 ? 's' : ''}`);
      }
      
      setShowClientListModal(false);
      setSelectionMode(false);
      setSelectedRooms([]);
      
    } catch (error) {
      console.error('Failed to add to client list:', error);
      toast.error('Failed to add to client list. Please try again.');
    }
  };

  const unreadCount = chats.filter(c => c.unread_count > 0).length;

  // Point 60 — PWA top-level tab badges (All / Favourites / Groups) count
  // unread CHATS, not messages, and are scoped to the selected MB filter
  // (and to any assigned_mbs restriction) the same way the chat list itself
  // is scoped, so the badge always matches what filtering to that tab
  // would actually show.
  const showMBBadgeFilter = !isInquiryHandler && (
    ['deposit', 'withdrawal', 'support'].includes(user?.chat_type || '') ||
    (isDepositHandler && ['deposit', 'withdrawal', 'support'].includes(chatTypeTab))
  );
  const assignedMbsForBadges = Array.isArray(user?.assigned_mbs) ? user.assigned_mbs : [];
  const mbScopedChatsForBadges = chats.filter(c => {
    if (c.is_archived) return false;
    if (showMBBadgeFilter && mbFilter !== 'all' && c.customer?.mb_setup !== mbFilter) return false;
    if (assignedMbsForBadges.length > 0) {
      const mb = c.customer?.mb_setup;
      if (!mb || !assignedMbsForBadges.includes(mb)) return false;
    }
    return true;
  });
  const allTabBadge = mbScopedChatsForBadges.filter(c => c.unread_count > 0).length;
  const favouritesTabBadge = mbScopedChatsForBadges.filter(c => c.is_favourite && c.unread_count > 0).length;
  const groupsSourceForBadge = isThirdPartyHandler
    ? (withdrawGroups || [])
    : (groups || []).filter(g => !String(g?.system_key || '').startsWith('withdraw_group'));
  const groupsTabBadge = groupsSourceForBadge.filter(g => (g.unread_count || 0) > 0).length;

  // Mini component to show bank preview in copy popup
  const CopyPopupBankPreview = ({ customerId, phone }) => {
    const [bankData, setBankData] = React.useState(null);
    React.useEffect(() => {
      axios.get(`${API}/clients/registered`).then(res => {
        const client = res.data.find(c => c.id === customerId || c.phone === phone);
        if (client) setBankData(client);
      }).catch(() => {});
    }, [customerId, phone]);
    if (!bankData) return <p className="text-xs text-gray-400">Loading bank details...</p>;
    if (!bankData.bank_name && !bankData.bank_account_number) return <p className="text-xs text-gray-400">No bank details saved</p>;
    return (
      <div className="text-xs text-gray-600 space-y-0.5">
        {bankData.bank_name && <p>Bank: {bankData.bank_name}</p>}
        {bankData.account_holder_name && <p>Holder: {bankData.account_holder_name}</p>}
        {bankData.bank_account_number && <p>A/c: {bankData.bank_account_number}</p>}
        {bankData.ifsc_code && <p>IFSC: {bankData.ifsc_code}</p>}
        {bankData.upi_id && <p>UPI: {bankData.upi_id}</p>}
      </div>
    );
  };

  // Web redesign (desktop) — panel meta for top-bar colored label + center MB.
  // Deposit handler switches panels via chatTypeTab; others fixed to chat_type.
  const HD_PANEL_META = {
    deposit: { label: 'Deposit panel', color: '#16a34a' },
    withdrawal: { label: 'Withdraw panel', color: '#e11d48' },
    support: { label: 'Support panel', color: '#2563eb' },
    inquiry: { label: 'Enquiry panel', color: '#7c3aed' },
  };
  const hdPanelKey = isDepositHandler ? chatTypeTab : (user?.chat_type || 'deposit');
  const hdPanelMeta = HD_PANEL_META[hdPanelKey] || HD_PANEL_META.deposit;
  const hdAssignedMbs = Array.isArray(user?.assigned_mbs) ? user.assigned_mbs : [];
  const hdCenterMB = (mbFilter && mbFilter !== 'all') ? mbFilter
    : (hdAssignedMbs.length === 1 ? hdAssignedMbs[0] : 'All MBs');
  const HD_CORNER_PANELS = [
    { key: 'deposit', letter: 'D', color: '#16a34a' },
    { key: 'withdrawal', letter: 'W', color: '#e11d48' },
  ].filter(p => p.key !== hdPanelKey);

  // Extracted so the SAME brand + action group render in the mobile in-column
  // header AND the desktop full-width top bar (mobile stays identical).
  const hdBrand = (
    <div
      className="cursor-pointer flex items-center gap-2 min-w-0 flex-shrink"
      onClick={() => { setSelectedChatId(null); setBottomTab('chats'); }}
    >
      <img src="/icons/icon-128x128.png" alt="DWS" className="w-9 h-9 rounded-full flex-shrink-0" />
      <div className="min-w-0">
        <h1 className="text-base sm:text-lg font-bold tracking-tight truncate leading-tight text-[#0d7a63]">DWS Support</h1>
        <p className="text-[11px] text-gray-400 font-medium truncate leading-tight">
          {isDepositHandler
            ? (chatTypeTab === 'inquiry' ? 'Enquiry Panel' : `${chatTypeTab.charAt(0).toUpperCase() + chatTypeTab.slice(1)} Panel`)
            : user?.chat_type
              ? (user.chat_type === 'inquiry' ? 'Enquiry Panel' : `${user.chat_type.charAt(0).toUpperCase() + user.chat_type.slice(1)} Panel`)
              : 'Handler Panel'}
        </p>
      </div>
    </div>
  );

  const hdHeaderActions = (
    <div className="flex items-center gap-0.5 sm:gap-1 flex-shrink-0">
      {!isThirdPartyHandler && !isInquiryHandler && <InstantAccessPW variant="light" />}
      {!isThirdPartyHandler && !isInquiryHandler && <InstantAccessN variant="light" />}
      {!isInquiryHandler && <InstantAccessG variant="light" />}
      {!isThirdPartyHandler && <InstantAccessPT variant="light" />}
      <button
        onClick={async () => {
          if (refreshing) return;
          setRefreshing(true);
          toast.info('Refreshing...');
          setRefreshTick(t => t + 1);
          await new Promise(r => setTimeout(r, 600));
          setRefreshing(false);
          toast.success('Refreshed!');
        }}
        className="p-1.5 hover:bg-gray-100 rounded-full text-gray-700 disabled:opacity-60"
        disabled={refreshing}
        title="Refresh"
        data-testid="handler-refresh-btn"
      >
        <ArrowClockwise size={20} weight="bold" className={refreshing ? 'animate-spin' : ''} />
      </button>
      <div className="flex items-center gap-2 relative">
        <div ref={menuRef} className="relative">
          <button onClick={() => setShowMenu(!showMenu)} className="p-1.5 hover:bg-gray-100 rounded-full text-gray-700">
            <DotsThreeVertical size={22} weight="bold" />
          </button>
          {showMenu && (
            <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg py-2 z-[60]">
              {isInquiryHandler && (user?.permissions?.create_client) && (
                <button onClick={() => { navigate('/create-client'); setShowMenu(false); }} className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100 flex items-center gap-2" data-testid="inquiry-create-client-btn">
                  <span className="text-lg">➕</span> Create Client
                </button>
              )}
              {isInquiryHandler && (user?.permissions?.enquiry_assign_mb) && (
                <button onClick={() => { navigate('/handler-settings?assign_mb=1'); setShowMenu(false); }} className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100 flex items-center gap-2" data-testid="inquiry-assign-mb-btn">
                  <span className="text-lg">📌</span> Assign to MB
                </button>
              )}
              {isInquiryHandler && (user?.permissions?.create_client || user?.permissions?.enquiry_assign_mb) && (
                <div className="border-t my-1"></div>
              )}
              <button
                onClick={async () => {
                  setShowMenu(false);
                  if (refreshing) return;
                  setRefreshing(true);
                  toast.info('Refreshing...');
                  setRefreshTick(t => t + 1);
                  await new Promise(r => setTimeout(r, 600));
                  setRefreshing(false);
                  toast.success('Refreshed!');
                }}
                className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                data-testid="handler-menu-refresh-btn"
              >
                <ArrowClockwise size={20} /> Refresh
              </button>
              <div className="border-t my-1"></div>
              <button onClick={() => { navigate('/groups'); setShowMenu(false); }} className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100 flex items-center gap-2" data-testid="handler-open-groups-btn">
                <UsersThree size={20} /> Groups
              </button>
              <button onClick={() => { navigate('/handler-settings'); setShowMenu(false); }} className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100 flex items-center gap-2" data-testid="handler-open-settings-btn">
                <Gear size={20} /> Settings
              </button>
              <div className="border-t my-1"></div>
              <button onClick={handleLogout} className="w-full px-4 py-2 text-left text-red-600 hover:bg-gray-100 flex items-center gap-2" data-testid="handler-logout-menu-btn">
                <SignOut size={20} /> Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Desktop full-width top bar (spans the whole screen): brand + center MB +
  // panel label + S/I toggles (deposit handler) + the shared action group.
  const hdFullBar = (
    <div className="bg-white text-gray-800 border-b border-gray-200 p-3 flex-shrink-0">
      <div className="flex items-center justify-between gap-2">
        {hdBrand}
        <div className="flex flex-col items-center gap-1 flex-shrink-0" data-testid="hd-topbar-center-mb">
          <div className="px-4 py-1 rounded-full bg-gray-50 border-2 text-[15px] font-extrabold tracking-wide" style={{ color: hdPanelMeta.color, borderColor: hdPanelMeta.color }}>{hdCenterMB}</div>
          <div className="text-[11px] font-bold px-3 py-0.5 rounded-full text-white" style={{ backgroundColor: hdPanelMeta.color }}>{hdPanelMeta.label}</div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {isDepositHandler && (
            <>
              <button onClick={() => { setChatTypeTab('support'); setMbFilter('all'); setSelectedChatId(null); }} title="Support panel" data-testid="hd-topbar-badge-S" className={`relative w-9 h-9 rounded-full text-white text-xs font-extrabold flex items-center justify-center ${chatTypeTab === 'support' ? 'ring-2 ring-white' : ''}`} style={{ backgroundColor: '#0d9488' }}>
                S
                {tabUnreadCounts.support > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center border border-white shadow" data-testid="hd-topbar-badge-S-unread">{tabUnreadCounts.support > 99 ? '99+' : tabUnreadCounts.support}</span>
                )}
              </button>
              <button onClick={() => { setChatTypeTab('inquiry'); setMbFilter('all'); setSelectedChatId(null); }} title="Inquiry panel" data-testid="hd-topbar-badge-I" className={`relative w-9 h-9 rounded-full text-white text-xs font-extrabold flex items-center justify-center ${chatTypeTab === 'inquiry' ? 'ring-2 ring-white' : ''}`} style={{ backgroundColor: '#7c3aed' }}>
                I
                {tabUnreadCounts.inquiry > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center border border-white shadow" data-testid="hd-topbar-badge-I-unread">{tabUnreadCounts.inquiry > 99 ? '99+' : tabUnreadCounts.inquiry}</span>
                )}
              </button>
            </>
          )}
          {hdHeaderActions}
        </div>
      </div>
    </div>
  );

  return (
    (() => {
      const useMobileWA = !isDesktop && !selectedChatId && bottomTab === 'chats'
        && ['all', 'unread', 'favourites', 'groups', 'archive'].includes(activeTab);
      if (useMobileWA) {
        return (
          <MobileWhatsAppHandlerView
            user={user}
            isDepositHandler={isDepositHandler}
            chatTypeTab={chatTypeTab}
            setChatTypeTab={setChatTypeTab}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            filteredChats={filteredChats}
            unreadCounts={unreadCounts}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            handleChatClick={handleChatClick}
            handleLogout={handleLogout}
            mbFilter={mbFilter}
            setMbFilter={setMbFilter}
            mbOptions={liveMBs}
            chats={chats}
            unreadTabBadge={allTabBadge}
            favouritesTabBadge={favouritesTabBadge}
            groupsTabBadge={groupsTabBadge}
            selectionMode={selectionMode}
            selectedRooms={selectedRooms}
            cancelSelection={cancelSelection}
            handleLongPressStart={handleLongPressStart}
            handleLongPressEnd={handleLongPressEnd}
            handleTouchMove={handleTouchMove}
            toggleRoomSelection={toggleRoomSelection}
            setShowDeleteConfirm={setShowDeleteConfirm}
            showDeleteConfirm={showDeleteConfirm}
            handleDeleteRooms={handleDeleteRooms}
            handleToggleArchive={handleToggleArchive}
            onRefresh={handleRefresh}
            API={API}
          />
        );
      }
      return (
    <div className="h-screen flex flex-col bg-white">
      {/* Web redesign (desktop) — FULL-WIDTH top bar spanning the whole screen */}
      {isDesktop && hdFullBar}

      <div className={`flex flex-1 min-h-0 ${isDesktop ? 'flex-row' : 'flex-col'}`}>
      <div
        className={`flex flex-col relative bg-white ${isDesktop ? 'border-r border-gray-200 flex-shrink-0 overflow-visible z-40 h-full' : 'flex-1 h-screen'}`}
        style={isDesktop ? { width: `${Math.max(300, listWidth || 380)}px` } : undefined}
      >
      {/* Web redesign (desktop) — draggable divider (always split layout) */}
      {isDesktop && (
        <div
          onMouseDown={startListResize}
          title="Drag to resize"
          data-testid="hd-list-resize-handle"
          className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize z-20 hover:bg-[#008069]/30 active:bg-[#008069]/50"
        />
      )}
      {/* Header — mobile keeps title+search; desktop shows search only (title
          lives in the full-width bar above). */}
      <div className={`${isDesktop ? 'bg-white border-b border-gray-200' : 'bg-[#008069] text-white'} p-3 flex-shrink-0`}>
        {!isDesktop && (
        <div className="flex items-center justify-between gap-2">
          <div 
            className="cursor-pointer flex items-center gap-2 min-w-0 flex-shrink"
            onClick={() => { setSelectedChatId(null); setBottomTab('chats'); }}
          >
            <img src="/icons/icon-128x128.png" alt="DWS" className="w-8 h-8 rounded-full flex-shrink-0" />
            <div className="min-w-0">
              <h1 className="text-base sm:text-lg font-bold tracking-tight truncate leading-tight">DWS Support</h1>
              <p className="text-[11px] text-white/70 font-medium truncate leading-tight">
              {isDepositHandler
                ? (chatTypeTab === 'inquiry'
                    ? 'Enquiry Panel'
                    : `${chatTypeTab.charAt(0).toUpperCase() + chatTypeTab.slice(1)} Panel`)
                : user?.chat_type
                  ? (user.chat_type === 'inquiry'
                      ? 'Enquiry Panel'
                      : `${user.chat_type.charAt(0).toUpperCase() + user.chat_type.slice(1)} Panel`)
                  : 'Handler Panel'}
            </p>
            </div>
          </div>
          {/* Right-side action group — G, PT, and menu all together so they
              never overlap the title on narrow phone widths. */}
          <div className="flex items-center gap-0.5 sm:gap-1 flex-shrink-0">
            {/* Session-48h: Inquiry handler only sees PT (pending enquiries)
                — no PW / N / G badges. Rest of the world sees the usual mix. */}
            {!isThirdPartyHandler && !isInquiryHandler && <InstantAccessPW />}
            {!isThirdPartyHandler && !isInquiryHandler && <InstantAccessN />}
            {!isInquiryHandler && <InstantAccessG />}
            {!isThirdPartyHandler && <InstantAccessPT />}
            {/* Session-41: manual refresh — mirrors AdminDashboard's icon.
                Bumps `refreshTick` which is a dep of the main data-loading
                useEffect, so all chats + unread counts + polling restart. */}
            <button
              onClick={async () => {
                if (refreshing) return;
                setRefreshing(true);
                toast.info('Refreshing...');
                setRefreshTick(t => t + 1);
                // Small delay so the spinner actually shows and toast lands
                await new Promise(r => setTimeout(r, 600));
                setRefreshing(false);
                toast.success('Refreshed!');
              }}
              className="p-1.5 hover:bg-green-600 rounded-full disabled:opacity-60"
              disabled={refreshing}
              title="Refresh"
              data-testid="handler-refresh-btn"
            >
              <ArrowClockwise size={20} weight="bold" className={refreshing ? 'animate-spin' : ''} />
            </button>
            <div className="flex items-center gap-2 relative">
            <div ref={menuRef} className="relative">
              <button 
                onClick={() => setShowMenu(!showMenu)}
                className="p-1.5 hover:bg-green-600 rounded-full"
              >
                <DotsThreeVertical size={22} weight="bold" />
              </button>

              {/* Dropdown Menu — Session-37: STRICTLY only two entries for
                  handlers: Settings + Logout. Every other capability (Groups,
                  Broadcast, Client List, Refresh) is either promoted into
                  Settings tiles (permission-gated) or removed to enforce the
                  new lean permission matrix. */}
              {showMenu && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg py-2 z-50">
                  {/* Session-48h — Inquiry handler quick actions.
                      "Create Client" and "Assign to MB" appear only when the
                      admin has granted the respective permission. Everything
                      else (Client List / Reports / Broadcast) is hidden. */}
                  {isInquiryHandler && (user?.permissions?.create_client) && (
                    <button
                      onClick={() => { navigate('/create-client'); setShowMenu(false); }}
                      className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                      data-testid="inquiry-create-client-btn"
                    >
                      <span className="text-lg">➕</span> Create Client
                    </button>
                  )}
                  {isInquiryHandler && (user?.permissions?.enquiry_assign_mb) && (
                    <button
                      onClick={() => { navigate('/handler-settings?assign_mb=1'); setShowMenu(false); }}
                      className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                      data-testid="inquiry-assign-mb-btn"
                    >
                      <span className="text-lg">📌</span> Assign to MB
                    </button>
                  )}
                  {isInquiryHandler && (user?.permissions?.create_client || user?.permissions?.enquiry_assign_mb) && (
                    <div className="border-t my-1"></div>
                  )}
                  {/* Fix doc #6 — Refresh also available inside the menu (the
                      standalone header icon stays too). Same action: bump refreshTick. */}
                  <button
                    onClick={async () => {
                      setShowMenu(false);
                      if (refreshing) return;
                      setRefreshing(true);
                      toast.info('Refreshing...');
                      setRefreshTick(t => t + 1);
                      await new Promise(r => setTimeout(r, 600));
                      setRefreshing(false);
                      toast.success('Refreshed!');
                    }}
                    className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                    data-testid="handler-menu-refresh-btn"
                  >
                    <ArrowClockwise size={20} />
                    Refresh
                  </button>
                  <div className="border-t my-1"></div>
                  <button
                    onClick={() => {
                      navigate('/groups');
                      setShowMenu(false);
                    }}
                    className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                    data-testid="handler-open-groups-btn"
                  >
                    <UsersThree size={20} />
                    Groups
                  </button>
                  <button
                    onClick={() => {
                      setShowPendingManager(true);
                      setShowMenu(false);
                    }}
                    className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                    data-testid="handler-open-pending-manager-btn"
                  >
                    <Broom size={20} />
                    Pending Manager
                  </button>
                  <button
                    onClick={() => {
                      navigate('/handler-settings');
                      setShowMenu(false);
                    }}
                    className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                    data-testid="handler-open-settings-btn"
                  >
                    <Gear size={20} />
                    Settings
                  </button>
                  <div className="border-t my-1"></div>
                  <button
                    onClick={handleLogout}
                    className="w-full px-4 py-2 text-left text-red-600 hover:bg-gray-100 flex items-center gap-2"
                    data-testid="handler-logout-menu-btn"
                  >
                    <SignOut size={20} />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
          </div>
        </div>
        )}

        {/* Search Bar */}
        <div className="mt-3 flex items-center gap-2">
          <div className="relative flex-1">
            <MagnifyingGlass size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search customers by name, phone or Client Code…"
              className={`w-full pl-10 pr-4 py-2 rounded-lg text-gray-800 placeholder-gray-500 focus:outline-none ${isDesktop ? 'bg-gray-100 border border-gray-200' : 'bg-white'}`}
            />
          </div>
          {/* Web redesign (desktop) — Deposit/Withdraw corner toggle (deposit handler only) */}
          {isDesktop && isDepositHandler && HD_CORNER_PANELS.map(p => (
            <button
              key={p.key}
              onClick={() => { setChatTypeTab(p.key); setMbFilter('all'); setSelectedChatId(null); }}
              title={`Go to ${p.key} panel`}
              data-testid={`hd-corner-panel-${p.key}`}
              className="relative w-9 h-9 flex-shrink-0 rounded-full text-white text-[14px] font-extrabold flex items-center justify-center shadow hover:opacity-90"
              style={{ backgroundColor: p.color }}
            >
              {p.letter}
              {tabUnreadCounts[p.key] > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center border border-white shadow" data-testid={`hd-corner-panel-${p.key}-unread`}>
                  {tabUnreadCounts[p.key] > 99 ? '99+' : tabUnreadCounts[p.key]}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Chat Type Tabs for Deposit Handler (hidden on desktop — panel switching via corner buttons + S/I badges) */}
      {isDepositHandler && (
        <div className={`bg-white border-b flex-shrink-0 ${isDesktop ? 'hidden' : 'flex'}`}>
          <button
            onClick={() => setChatTypeTab('deposit')}
            className={`flex-1 py-2.5 text-sm font-semibold transition-colors relative ${
              chatTypeTab === 'deposit'
                ? 'text-green-600 border-b-2 border-green-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Deposit
            {tabUnreadCounts.deposit > 0 && (
              <span className="absolute top-0.5 right-1/2 translate-x-4 min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center border border-white shadow" data-testid="tab-badge-deposit">
                {tabUnreadCounts.deposit > 99 ? '99+' : tabUnreadCounts.deposit}
              </span>
            )}
          </button>
          <button
            onClick={() => setChatTypeTab('withdrawal')}
            className={`flex-1 py-2.5 text-sm font-semibold transition-colors relative ${
              chatTypeTab === 'withdrawal'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Withdrawal
            {tabUnreadCounts.withdrawal > 0 && (
              <span className="absolute top-0.5 right-1/2 translate-x-4 min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center border border-white shadow" data-testid="tab-badge-withdrawal">
                {tabUnreadCounts.withdrawal > 99 ? '99+' : tabUnreadCounts.withdrawal}
              </span>
            )}
          </button>
          <button
            onClick={() => setChatTypeTab('support')}
            className={`flex-1 py-2.5 text-sm font-semibold transition-colors relative ${
              chatTypeTab === 'support'
                ? 'text-purple-600 border-b-2 border-purple-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Support
            {tabUnreadCounts.support > 0 && (
              <span className="absolute top-0.5 right-1/2 translate-x-4 min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center border border-white shadow" data-testid="tab-badge-support">
                {tabUnreadCounts.support > 99 ? '99+' : tabUnreadCounts.support}
              </span>
            )}
          </button>
          {/* Session-48h: Enquiry is now the 4th top-level slide tab
              (was buried as a support sub-tab). Cyan accent to distinguish. */}
          <button
            onClick={() => setChatTypeTab('inquiry')}
            className={`flex-1 py-2.5 text-sm font-semibold transition-colors relative ${
              chatTypeTab === 'inquiry'
                ? 'text-cyan-600 border-b-2 border-cyan-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            data-testid="handler-tab-enquiry"
          >
            Enquiry
            {tabUnreadCounts.inquiry > 0 && (
              <span className="absolute top-0.5 right-1/2 translate-x-4 min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center border border-white shadow" data-testid="tab-badge-inquiry">
                {tabUnreadCounts.inquiry > 99 ? '99+' : tabUnreadCounts.inquiry}
              </span>
            )}
          </button>
        </div>
      )}

      {/* Session-48h: Support sub-tabs REMOVED — Enquiry moved out to its
          own top-level slide tab. Left the block commented so the git diff
          is auditable, but no UI rendered here anymore. */}

      {/* Archive View Header — replaces filter tabs when viewing archive */}
      {activeTab === 'archive' ? (
        <div className="bg-white border-b flex items-center gap-3 px-4 py-2.5 flex-shrink-0" data-testid="handler-archive-header">
          <button
            onClick={() => {
              // Restore filter tab that user was on before entering Archive
              let prev = 'all';
              try {
                const saved = sessionStorage.getItem('hdl_tab_before_archive');
                if (saved && ['all', 'unread', 'favourites', 'groups'].includes(saved)) prev = saved;
                sessionStorage.removeItem('hdl_tab_before_archive');
              } catch (_) {}
              setActiveTab(prev);
            }}
            className="hover:bg-gray-100 p-1.5 rounded-full transition-colors"
            data-testid="handler-archive-back-btn"
            aria-label="Back to all chats"
          >
            <ArrowLeft size={22} weight="bold" className="text-gray-700" />
          </button>
          <div className="flex items-center gap-2">
            <span className="text-lg">📦</span>
            <h2 className="font-semibold text-gray-800">
              Archived <span className="text-sm text-gray-500 font-normal">({chats.filter(c => c.is_archived).length})</span>
            </h2>
          </div>
        </div>
      ) : (
      /* Filter Tabs */
      <div className="bg-white border-b flex gap-4 px-4 py-2 overflow-x-auto flex-shrink-0">
        {!isThirdPartyHandler && (
          <button
            onClick={() => setActiveTab('all')}
            className={`px-3 py-1 rounded-full whitespace-nowrap flex items-center gap-1 ${
              activeTab === 'all' ? 'bg-green-100 text-green-700 font-semibold' : 'text-gray-600'
            }`}
          >
            All {allTabBadge > 0 && <span className="text-xs">{allTabBadge}</span>}
          </button>
        )}
        {!isThirdPartyHandler && (
          <button
            onClick={() => setActiveTab('unread')}
            className={`px-3 py-1 rounded-full whitespace-nowrap flex items-center gap-1 ${
              activeTab === 'unread' ? 'bg-green-100 text-green-700 font-semibold' : 'text-gray-600'
            }`}
          >
            Unread {unreadCount > 0 && <span className="text-xs">{unreadCount}</span>}
          </button>
        )}
        {!isThirdPartyHandler && chatTypeTab !== 'inquiry' && (
          <button
            onClick={() => setActiveTab('favourites')}
            className={`px-3 py-1 rounded-full whitespace-nowrap flex items-center gap-1 ${
              activeTab === 'favourites' ? 'bg-green-100 text-green-700 font-semibold' : 'text-gray-600'
            }`}
          >
            Favourites {favouritesTabBadge > 0 && <span className="text-xs">{favouritesTabBadge}</span>}
          </button>
        )}
        {chatTypeTab !== 'inquiry' && (
        <button
            onClick={() => isThirdPartyHandler ? setActiveTab('groups') : navigate('/groups')}
            className={`px-3 py-1 rounded-full whitespace-nowrap flex items-center gap-1 ${
              activeTab === 'groups' ? 'bg-red-100 text-red-700 font-semibold' : 'text-gray-600'
            }`}
            data-testid="handler-groups-tab"
          >
            Groups {groupsTabBadge > 0 && <span className="text-xs">{groupsTabBadge}</span>}
        </button>
        )}
        {!isThirdPartyHandler && chats.filter(c => c.is_archived).length > 0 && (
          <button
            onClick={() => {
              if (activeTab === 'archive') {
                setActiveTab('all');
              } else {
                try { sessionStorage.setItem('hdl_tab_before_archive', activeTab); } catch (_) {}
                setActiveTab('archive');
              }
            }}
            className={`px-3 py-1 rounded-full whitespace-nowrap flex items-center gap-1 ${
              activeTab === 'archive' ? 'bg-gray-200 text-gray-800 font-semibold' : 'text-gray-600'
            }`}
            data-testid="handler-archive-tab"
          >
            📦 Archived <span className="text-xs">({chats.filter(c => c.is_archived).length})</span>
          </button>
        )}
      </div>
      )}

      {/* MB Setup Tabs — visible in Deposit / Withdrawal / Support views (never in Enquiry) */}
      {!isInquiryHandler && chatTypeTab !== 'inquiry' && activeTab !== 'groups' && (['deposit', 'withdrawal', 'support'].includes(user?.chat_type || '') || (isDepositHandler && ['deposit', 'withdrawal', 'support'].includes(chatTypeTab))) && (
        <div className={`bg-white border-b border-blue-100 flex items-center gap-2 px-4 py-2 flex-shrink-0 relative ${isDesktop ? '' : 'overflow-x-auto'}`} data-testid="mb-tabs-handler">
          <button
            onClick={() => { if (isDesktop) { setShowMbDropdown(v => !v); } else { setMbFilter('all'); } }}
            className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${
              mbFilter === 'all' ? 'bg-blue-600 text-white' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
            }`}
            data-testid="mb-tab-all"
          >
            {isDesktop ? `${mbFilter === 'all' ? `All Setups (${chats.length})` : mbFilter} ▾` : `All Setups (${chats.length})`}
          </button>
          {isDesktop && showMbDropdown && (
            <div className="absolute left-4 top-11 z-[60] bg-white border rounded-lg shadow-xl py-1 w-48 max-h-80 overflow-auto" data-testid="mb-dropdown">
              <button onClick={() => { setMbFilter('all'); setShowMbDropdown(false); }} className={`w-full text-left px-3 py-2 text-[13px] hover:bg-blue-50 ${mbFilter === 'all' ? 'text-blue-700 font-bold' : 'text-gray-700'}`}>
                All Setups ({chats.length})
              </button>
              {liveMBs
                .filter(mb => { const am = Array.isArray(user?.assigned_mbs) ? user.assigned_mbs : []; return am.length === 0 || am.includes(mb); })
                .map((mb) => {
                  const c = chats.filter(ch => ch.customer?.mb_setup === mb).length;
                  return (
                    <button key={mb} onClick={() => { setMbFilter(mb); setShowMbDropdown(false); }} className={`w-full text-left px-3 py-2 text-[13px] hover:bg-blue-50 ${mbFilter === mb ? 'text-blue-700 font-bold' : 'text-gray-700'}`} data-testid={`mb-drop-${mb}`}>
                      {mb} ({c})
                    </button>
                  );
                })}
            </div>
          )}
          {!isDesktop && liveMBs
            .filter(mb => {
              const am = Array.isArray(user?.assigned_mbs) ? user.assigned_mbs : [];
              return am.length === 0 || am.includes(mb);
            })
            .map((mb) => {
            const bucket = chats.filter(c => c.customer?.mb_setup === mb);
            const count = bucket.length;
            // Point 30 — badge is a count of unread CHATS in this MB, not a
            // sum of unread messages across them.
            const unread = bucket.filter(c => (c.unread_count || 0) > 0).length;
            const isActive = mbFilter === mb;
            return (
              <button
                key={mb}
                onClick={() => setMbFilter(mb)}
                className={`relative px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap transition-colors flex items-center gap-1.5 ${
                  isActive ? 'bg-blue-600 text-white' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                }`}
                data-testid={`mb-tab-${mb}`}
              >
                <span>{mb} ({count})</span>
                {unread > 0 && (
                  <span
                    className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold ${
                      isActive ? 'bg-white text-red-600' : 'bg-red-500 text-white'
                    }`}
                    data-testid={`mb-unread-${mb}`}
                  >
                    {unread > 99 ? '99+' : unread}
                  </span>
                )}
              </button>
            );
          })}
          {/* "G" chip — dropdown listing MB-wise Withdraw Groups (mobile only;
              desktop web uses the G badge in the full-width top bar instead) */}
          {!isDesktop && withdrawGroups.length > 0 && (
            <>
              <button
                ref={(el) => { if (el) window.__gBtnRef = el; }}
                onClick={(e) => {
                  const r = e.currentTarget.getBoundingClientRect();
                  setGMenuPos({ top: r.bottom + 4, left: r.left });
                  setShowWithdrawGroupsMenu(v => !v);
                }}
                className="px-2.5 py-1 rounded-full text-xs font-bold whitespace-nowrap bg-amber-500 text-white hover:bg-amber-600 flex items-center gap-1"
                data-testid="withdraw-group-shortcut-handler"
                title="Withdraw Groups"
              >
                G ▾
              </button>
              {showWithdrawGroupsMenu && (
                <>
                  <div className="fixed inset-0 z-[999]" onClick={() => setShowWithdrawGroupsMenu(false)} />
                  <div
                    className="fixed bg-white rounded-lg shadow-xl border border-gray-200 z-[1000] min-w-[210px] py-1"
                    style={{ top: gMenuPos.top, left: gMenuPos.left }}
                    data-testid="withdraw-groups-menu"
                  >
                    <div className="px-3 py-1.5 text-[10px] font-bold text-amber-600 uppercase border-b border-gray-100">Withdraw Groups</div>
                    {withdrawGroups.map(g => (
                      <button
                        key={g.id}
                        onClick={() => {
                          setShowWithdrawGroupsMenu(false);
                          const from = window.location.pathname + window.location.search + window.location.hash;
                          navigate(`/groups/${g.id}`, {
                            state: { from, fromState: { selectedChatId, chatTypeTab } }
                          });
                        }}
                        className="w-full text-left px-3 py-2 hover:bg-amber-50 text-xs font-semibold text-gray-800 flex items-center justify-between"
                        data-testid={`withdraw-group-mb-${g.mb_setup || 'legacy'}`}
                      >
                        <span>{g.name}</span>
                        {g.last_message_at && <span className="text-[9px] text-gray-400">●</span>}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </>
          )}
          <button
            onClick={() => setShowBulkMB(true)}
            className="ml-auto shrink-0 px-3 py-1 rounded-full text-xs font-semibold bg-teal-600 text-white hover:bg-teal-700 whitespace-nowrap"
            data-testid="bulk-mb-open-handler"
          >
            Bulk Assign
          </button>
        </div>
      )}
      {showBulkMB && (
        <BulkAssignMBModal
          open={showBulkMB}
          onClose={() => setShowBulkMB(false)}
          clients={chats.map(c => ({
            id: c.customer?.id,
            name: c.customer?.name,
            phone: c.customer?.phone,
            mb_setup: c.customer?.mb_setup,
            unread_count: c.unread_count || 0,
          }))}
          actorId={user?.id}
          onDone={(ids, mbValue) => {
            // Optimistic update: patch mb_setup in local chats
            setChats(prev => prev.map(c => (
              ids.includes(c.customer?.id)
                ? { ...c, customer: { ...c.customer, mb_setup: mbValue } }
                : c
            )));
          }}
        />
      )}

      {/* Main Content Area */}
      {bottomTab === 'updates' ? (
        /* Updates Tab Content - Status + Broadcast */
        <div className="flex-1 overflow-y-auto bg-gray-50">
          <div className="max-w-2xl mx-auto p-4">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Updates</h2>
            
            {/* Status Management (Inline) */}
            <div className="mb-4">
              <StatusManagement user={user} />
            </div>

            {/* Broadcast Section */}
            <div className="bg-white rounded-xl shadow-sm">
              <div className="p-4 border-b">
                <h3 className="font-semibold text-gray-700">Broadcast</h3>
              </div>
              <div 
                className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => navigate('/broadcast')}
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                    <Megaphone size={24} className="text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">Broadcast Messages</p>
                    <p className="text-sm text-gray-500">Send message to multiple clients</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : bottomTab === 'settings' ? (
        /* Settings Tab Content */
        <div className="flex-1 overflow-y-auto bg-gray-50">
          <div className="max-w-2xl mx-auto p-4">
            {/* Back Button + Header */}
            <div className="flex items-center gap-3 mb-6">
              <button
                onClick={() => setBottomTab('chats')}
                className="hover:bg-gray-200 p-2 rounded-full transition-colors"
              >
                <ArrowLeft size={24} weight="bold" className="text-gray-700" />
              </button>
              <h2 className="text-2xl font-bold text-gray-800">Handler Settings</h2>
            </div>
            
            {/* Create Client Button */}
            <button
              onClick={() => navigate('/handler/create-client')}
              className="w-full bg-cyan-500 text-white py-4 rounded-lg font-semibold flex items-center justify-center gap-3 hover:bg-cyan-600 transition-colors mb-4 shadow-md"
            >
              <span className="text-2xl">➕</span>
              <span>Create Client Account</span>
              <span className="bg-white/20 px-2 py-1 rounded-full text-xs">New</span>
            </button>
            
            {/* Inquiry Chats Section - Only for Deposit Handler */}
            {isDepositHandler && (
              <>
              {/* Inactive Clients Tracker Button */}
              <button
                onClick={() => navigate('/inactive-clients')}
                data-testid="inactive-clients-btn"
                className="w-full bg-red-500 text-white py-4 rounded-lg font-semibold flex items-center justify-center gap-3 hover:bg-red-600 transition-colors mb-4 shadow-md"
              >
                <span className="text-xl">⏰</span>
                <div className="text-left">
                  <span>Inactive Clients Tracker</span>
                  <span className="ml-2 bg-white/20 px-2 py-1 rounded-full text-xs">PDF Export</span>
                </div>
              </button>

              <div className="bg-white rounded-lg shadow-sm mb-4 overflow-hidden">
                <button
                  onClick={() => {
                    setShowInquiryInSettings(!showInquiryInSettings);
                    if (!showInquiryInSettings) loadInquiryChats();
                  }}
                  className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xl">💬</span>
                    <div className="text-left">
                      <h3 className="font-semibold text-gray-800">Inquiry Chats</h3>
                      <p className="text-xs text-gray-500">View all inquiry conversations</p>
                    </div>
                  </div>
                  <span className="text-gray-400 text-lg">{showInquiryInSettings ? '▲' : '▼'}</span>
                </button>
                
                {showInquiryInSettings && (
                  <div className="border-t max-h-80 overflow-y-auto">
                    {inquiryChats.length === 0 ? (
                      <p className="p-4 text-center text-gray-500 text-sm">No inquiry chats found</p>
                    ) : (
                      inquiryChats.map((chat) => (
                        <div
                          key={chat.room_id}
                          onClick={() => {
                            if (isDesktop) {
                              setSelectedChatId(chat.room_id);
                            } else {
                              navigate(`/handler-chat/${chat.room_id}`, { state: { chatTypeTab } });
                            }
                          }}
                          className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100"
                        >
                          <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                            <span className="text-orange-600 font-semibold text-sm">
                              {(chat.customer?.name || 'I')[0].toUpperCase()}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-gray-800 truncate text-sm">{chat.customer?.name || 'Inquiry Client'}</h4>
                            <p className="text-xs text-gray-500 truncate">{formatLastMessagePreview(chat.last_message, 'No messages')}</p>
                          </div>
                          {chat.unread_count > 0 && (
                            <span className="bg-orange-500 text-white text-xs rounded-full px-1.5 py-0.5 min-w-[20px] text-center">
                              {chat.unread_count}
                            </span>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
              </>
            )}
            
            {/* Status Management */}
            <div className="mb-4">
                <StatusManagement user={user} />
            </div>
            
            {/* Blocked Clients Section */}
            <div className="bg-white rounded-lg shadow-sm p-6 mb-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Blocked Clients</h3>
                <BlockedClientsSection userId={user.id} userRole={user.role} />
            </div>
          </div>
        </div>
      ) : (isInquiryHandler || chatTypeTab === 'inquiry' || user?.chat_type === 'inquiry') ? (
        /* Inquiry Panel — shared filter+list (web split via handleChatClick) */
        <div className="flex-1 overflow-y-auto bg-gray-50" data-testid="handler-inquiry-panel-wrap">
          <InquiryPanel user={user} onOpenChat={(rid) => handleChatClick({ room_id: rid, chat_type: 'inquiry' })} />
        </div>
      ) : (
        /* Chat List */
        <div ref={chatListScrollRef} onScroll={persistScroll} className="flex-1 overflow-y-auto" data-testid="handler-chat-list-scroll">
          {/* Archived box — shows the count of archived chats. Handler can
              tap to jump into the 'archive' filter tab (same UX as admin). */}
          {chats.filter(c => c.is_archived).length > 0 && activeTab !== 'archive' && (
            <div
              onClick={() => {
                try { sessionStorage.setItem('hdl_tab_before_archive', activeTab); } catch (_) {}
                setActiveTab('archive');
              }}
              className="bg-white border-b px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-gray-50 active:bg-gray-100 transition-colors"
              data-testid="handler-archived-box"
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                  <span className="text-2xl">📦</span>
                </div>
                <div>
                  <div className="font-semibold text-gray-800">Archived</div>
                  <div className="text-sm text-gray-500">
                    {chats.filter(c => c.is_archived).length} chats
                  </div>
                </div>
              </div>
              <div className="text-gray-400">›</div>
            </div>
          )}
          {/* Selection Mode Header */}
          {selectionMode && (
            <div className="sticky top-0 bg-green-500 text-white p-3 flex items-center justify-between z-10">
              <div className="flex items-center gap-2">
                <button onClick={cancelSelection} className="hover:bg-green-600 p-1 rounded">
                  ← Back
                </button>
                <span className="font-semibold">{selectedRooms.length} selected</span>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleTogglePinnedChat}
                  disabled={selectedRooms.length === 0}
                  className="bg-white/20 hover:bg-white/30 px-3 py-1 rounded disabled:opacity-50 text-sm font-medium"
                  data-testid="handler-selection-pin-btn"
                >
                  {selectedRooms.every(rid => chats.find(c => c.room_id === rid)?.is_pinned_chat) ? 'Unpin' : 'Pin'}
                </button>
                <button
                  onClick={handleToggleArchive}
                  disabled={selectedRooms.length === 0}
                  className="bg-white/20 hover:bg-white/30 px-3 py-1 rounded disabled:opacity-50 text-sm font-medium"
                  data-testid="handler-selection-archive-btn"
                >
                  {activeTab === 'archive' ? 'Unarchive' : 'Archive'}
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={selectedRooms.length === 0}
                  className="bg-red-500 hover:bg-red-600 px-3 py-1 rounded disabled:opacity-50 text-sm"
                  data-testid="handler-selection-delete-btn"
                >
                  Delete
                </button>
              </div>
            </div>
          )}

          {loading ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400">
              <div className="w-8 h-8 border-2 border-gray-300 border-t-blue-400 rounded-full animate-spin" />
            </div>
          ) : filteredChats.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-500">
              <Chats size={64} weight="light" />
              <p className="mt-4">No chats found</p>
            </div>
          ) : (
            <>
            {/* Session-38 perf: shared row renderer so the fallback `.map()`
                path and the virtualized path emit exactly the same JSX.
                `styleOverride` is only supplied by react-window and carries
                the absolute-positioning offsets it needs to place the row. */}
            {(() => {
              const renderChatRow = (chat, styleOverride) => {
                const isGroup = activeTab === 'groups';
                const itemId = isGroup ? chat.id : chat.room_id;
                const itemName = isGroup ? chat.name : chat.customer?.name;
                const itemProfilePic = isGroup ? chat.profile_pic : chat.customer?.profile_pic;
                return (
              <div
                key={itemId}
                style={styleOverride}
                onClick={() => {
                  if (selectionMode) {
                    toggleRoomSelection(itemId);
                  } else {
                    if (isGroup) {
                      handleGroupClick(chat.id);
                    } else {
                      handleChatClick(chat);
                    }
                  }
                }}
                onTouchStart={(e) => !selectionMode && handleLongPressStart(itemId, e)}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleLongPressEnd}
                onMouseDown={(e) => !selectionMode && handleLongPressStart(itemId, e)}
                onMouseMove={handleTouchMove}
                onMouseUp={handleLongPressEnd}
                onMouseLeave={handleLongPressEnd}
                className={`flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 transition-colors ${
                  selectedRooms.includes(itemId)
                    ? 'bg-green-50'
                    : (isDesktop && selectedChatId === itemId)
                      ? 'bg-[#f0f2f5] shadow-inner'
                      : ''
                }`}
              >
                {/* Selection Checkbox */}
                {selectionMode && (
                  <div className="flex-shrink-0">
                    <input
                      type="checkbox"
                      checked={selectedRooms.includes(itemId)}
                      onChange={() => toggleRoomSelection(itemId)}
                      className="w-5 h-5 text-green-600"
                    />
                  </div>
                )}

                {/* Profile Picture - WhatsApp colored placeholders */}
                <div
                  className="w-12 h-12 rounded-full flex-shrink-0 overflow-hidden flex items-center justify-center"
                  style={itemProfilePic ? {
                    backgroundImage: `url(${API}${itemProfilePic})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundColor: 'transparent',
                  } : (isDesktop && !isGroup ? (() => {
                    const pal = avatarPalFor(itemName || 'C');
                    return { backgroundColor: pal.bg, color: pal.fg };
                  })() : {
                    backgroundColor: (() => {
                      const colors = ['#00a884', '#53bdeb', '#e67e22', '#9b59b6', '#e74c3c', '#2ecc71', '#f39c12', '#1abc9c'];
                      const name = itemName || 'C';
                      return colors[name.charCodeAt(0) % colors.length];
                    })(),
                  })}
                >
                  {!itemProfilePic && (
                    <span className={`text-lg font-bold ${isDesktop && !isGroup ? '' : 'text-white'}`} style={isDesktop && !isGroup ? { color: avatarPalFor(itemName || 'C').fg } : undefined}>
                      {(itemName || 'C')[0]?.toUpperCase()}
                    </span>
                  )}
                </div>

                {/* Chat Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-1.5 min-w-0">
                      {!isGroup && chat.is_pinned_chat && (
                        <PushPin size={12} weight="fill" className="text-gray-400 flex-shrink-0" title="Pinned" data-testid={`pinned-icon-${chat.room_id}`} />
                      )}
                      {!isGroup && chat.customer?.is_priority && (
                        <span title="VIP Priority" className="text-amber-500 text-sm" data-testid={`vip-star-list-${chat.room_id}`}>★</span>
                      )}
                      <h3 className="font-semibold text-gray-800 truncate">{itemName || (isGroup ? 'Group' : 'Customer')}</h3>
                      {!isGroup && chat.customer?.flag && (
                        <FlagIcons flag={chat.customer.flag} size={12} title={chat.customer.flag === 'red' ? 'Suspicious' : 'Premium'} />
                      )}
                      {!isGroup && <MBSetupBadge value={chat.customer?.mb_setup} />}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {/* Copy Details Button */}
                      {!isGroup && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setCopyPopupChat(chat);
                          }}
                          className="p-0.5 hover:bg-gray-100 rounded transition-colors"
                          title="Copy details"
                        >
                          <Copy size={18} className="text-gray-400 hover:text-blue-500" />
                        </button>
                      )}
                      {/* WhatsApp Button */}
                      {!isGroup && chat.customer?.phone && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openWhatsApp(chat.customer.phone, chat.id);
                          }}
                          className="p-0.5 hover:bg-gray-100 rounded transition-colors"
                          title="Open WhatsApp"
                        >
                          <WhatsappLogo size={18} className="text-gray-400 hover:text-green-500" />
                        </button>
                      )}
                      <span className="text-xs text-gray-500">
                        {formatTime(isGroup ? chat.last_message_at : (chat.last_message_time || chat.last_message?.timestamp))}
                      </span>
                      {!isGroup && (unreadCounts[chat.room_id] || chat.unread_count) > 0 && (
                        <div className="bg-green-500 text-white text-xs font-bold rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center">
                          {(unreadCounts[chat.room_id] || chat.unread_count) > 99 ? '99+' : (unreadCounts[chat.room_id] || chat.unread_count)}
                        </div>
                      )}
                      {/* Session-42: pending service-request pill — shown
                          next to the unread badge when this client has an
                          open Deposit / Withdraw / Support request awaiting
                          approval or cancel. */}
                      {!isGroup && (chat.pending_sr_count || 0) > 0 && (
                        <div
                          className="bg-amber-500 text-white text-[10px] font-bold rounded-full min-w-[22px] h-5 px-1.5 flex items-center justify-center gap-0.5"
                          title={`${chat.pending_sr_count} pending request${chat.pending_sr_count === 1 ? '' : 's'}`}
                          data-testid={`chat-pending-sr-${chat.room_id}`}
                        >🎫{chat.pending_sr_count}</div>
                      )}
                      {/* Pending-entry count for withdraw MB groups (shown to
                          all staff — matches the count under the header `G`
                          badge so third-party handlers can see it at a glance). */}
                      {isGroup && Number(chat.pending_count || 0) > 0 && (
                        <div
                          className="bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center"
                          data-testid={`group-pending-count-${chat.id}`}
                          title={`${chat.pending_count} pending entries`}
                        >
                          {chat.pending_count > 99 ? '99+' : chat.pending_count}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-600 truncate flex-1 min-w-0">
                      {truncatePreview(isGroup
                        ? (chat.description || 'Group chat')
                        : formatLastMessagePreview(chat.last_message)
                      )}
                    </p>
                  </div>
                </div>
              </div>
                );
              };

              // Session-38 perf: virtualize only when the list is long enough
              // to benefit (100+ rows). Below that, the classic `.map()` +
              // progressive `visibleCount` path is faster + safer.
              const VIRTUALIZE_THRESHOLD = 100;
              const CHAT_ROW_HEIGHT = 76; // px — matches avatar (48) + p-3 padding (24) + border
              if (filteredChats.length > VIRTUALIZE_THRESHOLD) {
                return (
                  <div style={{ flex: 1, minHeight: 0 }} data-testid="handler-chatlist-virtualized">
                    <AutoSizer
                      renderProp={({ height, width }) => (
                        <VirtualList
                          rowCount={filteredChats.length}
                          rowHeight={CHAT_ROW_HEIGHT}
                          rowComponent={({ index, style }) => renderChatRow(filteredChats[index], style)}
                          rowProps={{}}
                          overscanCount={5}
                          style={{ height: height || 600, width: width || '100%' }}
                          defaultHeight={height || 600}
                        />
                      )}
                    />
                  </div>
                );
              }
              // Fallback path — small lists stay on the classic renderer so
              // nothing regresses for the 90% case.
              return (
                <>
                  {filteredChats.slice(0, visibleCount).map((chat) => renderChatRow(chat))}
                  {/* Progressive-render sentinel — loads next 30 rows on scroll */}
                  {visibleCount < filteredChats.length && (
                    <div
                      ref={chatListLoadMoreRef}
                      className="py-3 flex items-center justify-center text-[11px] text-gray-500"
                      data-testid="handler-chatlist-load-more"
                    >
                      <span className="w-3 h-3 border-2 border-gray-400 border-t-transparent rounded-full animate-spin mr-2"></span>
                      Loading more chats… ({filteredChats.length - visibleCount} remaining)
                    </div>
                  )}
                </>
              );
            })()}
            </>
          )}
        </div>
      )}

      {/* P10 (option b) — global bottom navigation REMOVED. Groups via top "G"
          tab; Settings / Groups via the header 3-dot menu. */}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full">
            <h3 className="text-lg font-semibold mb-2">Delete Chats?</h3>
            <p className="text-gray-600 mb-4">
              Are you sure you want to delete {selectedRooms.length} chat{selectedRooms.length > 1 ? 's' : ''}? 
              This will only remove {selectedRooms.length > 1 ? 'them' : 'it'} from your view. 
              The customer and admin will still see {selectedRooms.length > 1 ? 'these chats' : 'this chat'}.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteRooms}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Copy Details Popup - Login + Bank */}
      {copyPopupChat && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setCopyPopupChat(null)}>
          <div className="bg-white rounded-xl max-w-sm w-full p-5 shadow-2xl" onClick={e => e.stopPropagation()} data-testid="copy-details-popup">
            <h3 className="text-lg font-bold text-gray-800 mb-4">Copy Details</h3>
            
            {/* Login Details */}
            <div className="bg-gray-50 rounded-lg p-3 mb-3">
              <div className="flex items-center justify-between mb-2">
                <span className="font-semibold text-sm text-gray-700">Login Details</span>
                <button
                  onClick={async () => {
                    const c = copyPopupChat.customer || copyPopupChat;
                    let text = `Username: ${c.username || c.name || 'N/A'}\nPhone: ${c.phone || c.mobile_number || 'N/A'}\nPassword: Abcd1234`;
                    try {
                      const settingsRes = await axios.get(`${API}/settings`);
                      const loginLink = settingsRes.data?.customer_login_link;
                      if (loginLink) text += `\nLink: ${loginLink}`;
                    } catch (e) {}
                    navigator.clipboard.writeText(text);
                    toast.success('Login details copied!', { duration: 1500 });
                    setCopyPopupChat(null);
                  }}
                  className="px-3 py-1 bg-[#00a884] text-white text-xs rounded-full hover:bg-[#008f72]"
                >
                  Copy
                </button>
              </div>
              <p className="text-xs text-gray-600">Username: {(copyPopupChat.customer || copyPopupChat).username || (copyPopupChat.customer || copyPopupChat).name || 'N/A'}</p>
              <p className="text-xs text-gray-600">Phone: {(copyPopupChat.customer || copyPopupChat).phone || (copyPopupChat.customer || copyPopupChat).mobile_number || 'N/A'}</p>
              <p className="text-xs text-gray-600">Password: Abcd1234</p>
            </div>

            {/* Bank Details - Fetch from registered clients */}
            <div className="bg-gray-50 rounded-lg p-3 mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="font-semibold text-sm text-gray-700">Bank Details</span>
                <button
                  onClick={async () => {
                    try {
                      const customerId = (copyPopupChat.customer || copyPopupChat).id || copyPopupChat.customer_id;
                      const res = await axios.get(`${API}/clients/registered`);
                      const client = res.data.find(c => c.id === customerId || c.phone === (copyPopupChat.customer || copyPopupChat).phone);
                      if (!client || (!client.bank_name && !client.bank_account_number)) {
                        toast.error('No bank details found'); return;
                      }
                      let text = `User Name: ${client.username || 'N/A'}

A/c Holder Name: ${client.account_holder_name || client.client_name || 'N/A'}

Bank Account No: ${client.bank_account_number || 'N/A'}

IFSC Code: ${client.ifsc_code || 'N/A'}

Bank Name: ${client.bank_name || 'N/A'}`;
                      if (client.address) text += `\n\nAddress: ${client.address}`;
                      if (client.upi_id) text += `\n\nUPI ID: ${client.upi_id}`;
                      navigator.clipboard.writeText(text);
                      toast.success('Bank details copied!', { duration: 1500 });
                      setCopyPopupChat(null);
                    } catch (e) {
                      toast.error('Failed to fetch bank details');
                    }
                  }}
                  className="px-3 py-1 bg-[#00a884] text-white text-xs rounded-full hover:bg-[#008f72]"
                >
                  Copy
                </button>
              </div>
              <CopyPopupBankPreview customerId={(copyPopupChat.customer || copyPopupChat).id || copyPopupChat.customer_id} phone={(copyPopupChat.customer || copyPopupChat).phone} />
            </div>

            <button
              onClick={() => setCopyPopupChat(null)}
              className="w-full py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        </div>
      )}

      </div>

      {/* Right Panel — Desktop split view (Point 9). Always rendered on
          desktop: shows chat when open, else idle DWS logo. Back button
          inside chat → selectedChatId=null → idle logo, list stays. */}
      {isDesktop && (
        selectedChatId ? (
          <div className="flex-1 min-h-0 relative overflow-hidden flex flex-col">
            <HandlerChat
              key={selectedChatId}
              embeddedRoomId={selectedChatId}
              onBackToList={() => setSelectedChatId(null)}
            />
          </div>
        ) : (
          <div
            className="flex-1 min-h-0 flex flex-col items-center justify-center bg-[#f0f2f5] border-l border-gray-200"
            data-testid="handler-idle-panel"
          >
            <img src="/icons/customer/icon-192x192.png" alt="DWS Vault Chat" className="w-24 h-24 opacity-80 mb-4" />
            <h3 className="text-xl font-bold text-gray-700">DWS Vault Chat</h3>
            <p className="text-sm text-gray-400 mt-1">Select a chat to start messaging</p>
          </div>
        )
      )}

      <CustomerDetailsPanel />
      </div>{/* End inner row wrapper */}
      {showPendingManager && (
        <PendingManager user={user} onClose={() => setShowPendingManager(false)} />
      )}
    </div>
      );
    })()
  );
};

export default HandlerDashboard;
